import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import type { AITripPreferenceStep } from './types'

export interface LocationFact {
  text: ReactNode
  detail?: ReactNode
}

type LocationFactsMap = {
  [key: string]: Array<LocationFact>
}

export const BLACK_LISTED_TRIP_GENERATION_LOCATIONS = [
  'Maldives',
]

export const locationFacts: LocationFactsMap = {
  Sydney: [
    {
      text: (
        <FormattedMessage
          id="4BIdW4q43fsSZXkgASP2"
          defaultMessage="The Sydney Opera House uses 1,056,006 roof tiles"
          description="Fun fact headline shown while AI trip generation is in progress for Sydney (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="kMMPQvuFNolc0y0iGx82"
          defaultMessage="Those iconic white tiles are made from a Swedish porcelain — and they actually clean themselves in the rain!"
          description="Fun fact detail shown while AI trip generation is in progress for Sydney (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="8HQMkidOpT5FFtPHoE9l"
          defaultMessage="Sydney Harbour is the world’s largest natural harbour"
          description="Fun fact headline shown while AI trip generation is in progress for Sydney (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="H65_V5xWKDHF3x3XEG0R"
          defaultMessage="It stretches over 240 km of shoreline and holds more water than the world’s next four largest natural harbours combined."
          description="Fun fact detail shown while AI trip generation is in progress for Sydney (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="LTNv2eZJjZdPL-HOsWKO"
          defaultMessage="Enough steel went into the Sydney Harbour Bridge to build eight Eiffel Towers"
          description="Fun fact headline shown while AI trip generation is in progress for Sydney (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="NeCJLaug8weQvLvB2-Rf"
          defaultMessage="Its arch spans 503 m and the top is 134 m above sea level at mid-span."
          description="Fun fact detail shown while AI trip generation is in progress for Sydney (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="b-4WoD2bZmz7Y_H98o7X"
          defaultMessage="The world’s first surf lifesaving club was founded at Bondi Beach in 1907"
          description="Fun fact headline shown while AI trip generation is in progress for Sydney (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="fhYwMXHbaIJz6RNB8cE_"
          defaultMessage="Those red-and-yellow flags you see started right here."
          description="Fun fact detail shown while AI trip generation is in progress for Sydney (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="kIl7KKRaq-FCRovYTCJZ"
          defaultMessage="Sydney’s Royal Botanic Garden is Australia’s oldest scientific institution"
          description="Fun fact headline shown while AI trip generation is in progress for Sydney (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="RNaY88SM4NHc7yD55ciO"
          defaultMessage="It’s been cultivating exotic and native plants since 1816."
          description="Fun fact detail shown while AI trip generation is in progress for Sydney (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="LSwYmA7iWp1x_3Tnl_Ou"
          defaultMessage="There’s a secret beach at Milk Beach in Vaucluse"
          description="Fun fact headline shown while AI trip generation is in progress for Sydney (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="HKOKbrFSVXr1o0rvAE1z"
          defaultMessage="Tucked beneath the cliffs of Watsons Bay, it’s reached via a short but steep walking path."
          description="Fun fact detail shown while AI trip generation is in progress for Sydney (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="hhygEqyErGVs--lgxgRj"
          defaultMessage="Humpback whales cruise past Cape Solander during their annual migration"
          description="Fun fact headline shown while AI trip generation is in progress for Sydney (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Gb9LILQZuku85q-qQthO"
          defaultMessage="From May to November, thousands of humpbacks hug the coast — volunteers log more than 3,000 sightings each season."
          description="Fun fact detail shown while AI trip generation is in progress for Sydney (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="zoXlP8MObX-yEOGQyV8R"
          defaultMessage="The Rocks district is Sydney’s oldest neighbourhood"
          description="Fun fact headline shown while AI trip generation is in progress for Sydney (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="_Gii_KgCfyJvzAAKIGIn"
          defaultMessage="Walk its cobbled laneways to feel like you’ve stepped back into the 1800s—complete with hidden pubs."
          description="Fun fact detail shown while AI trip generation is in progress for Sydney (fact 8)"
        />
      ),
    },
  ],
  Melbourne: [
    {
      text: (
        <FormattedMessage
          id="nkdexudJClBGLBFJOlg6"
          defaultMessage="Melbourne has the largest tram network in the world"
          description="Fun fact headline shown while AI trip generation is in progress for Melbourne (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="p7ZqnjmecsKo0zVxZWh1"
          defaultMessage="More than 250 km of double track spider across the suburbs, linking 1,700 stops. The oldest section opened in 1884 and some heritage W-class trams still rumble along city routes."
          description="Fun fact detail shown while AI trip generation is in progress for Melbourne (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="xB1DaxlJfanW4LK5rw3J"
          defaultMessage="Melbourne’s Royal Exhibition Building is UNESCO-listed"
          description="Fun fact headline shown while AI trip generation is in progress for Melbourne (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ZdpIaqdmWwxC2lIe-R8b"
          defaultMessage="Completed in 1880 for the International Exhibition, it hosted Australia’s first Federal Parliament in 1901. Its soaring dome was inspired by Florence’s Duomo."
          description="Fun fact detail shown while AI trip generation is in progress for Melbourne (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="NJeiqHjxoSAwV93aDkj-"
          defaultMessage="The city’s coffee culture rivals any in the world"
          description="Fun fact headline shown while AI trip generation is in progress for Melbourne (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="2MHfHDefnjVPhyb08wKD"
          defaultMessage="Melburnians consume more espresso per capita than most global cities. Annual competitions like the World Barista Championship often feature Aussie finalists."
          description="Fun fact detail shown while AI trip generation is in progress for Melbourne (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="mFn8rG3yBxH1DZN2Appb"
          defaultMessage="Melbourne hosts the Australian Open tennis tournament"
          description="Fun fact headline shown while AI trip generation is in progress for Melbourne (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="b_mFO6Zjea1OviLe5UNJ"
          defaultMessage="As the first Grand Slam of the year, it attracts hundreds of thousands of fans each January. Matches continue late into balmy summer nights under retractable-roof arenas."
          description="Fun fact detail shown while AI trip generation is in progress for Melbourne (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="2RI3hw-cUAp4npivTfyF"
          defaultMessage="The Yarra River once flowed “upside-down”"
          description="Fun fact headline shown while AI trip generation is in progress for Melbourne (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="9PxHJ4Ha0XGpeRQasBhW"
          defaultMessage="Early European settlers nicknamed it that because heavy clay silt made the water look brown and opaque. Extensive river-clean-up projects have since improved clarity."
          description="Fun fact detail shown while AI trip generation is in progress for Melbourne (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="7bRwT735qaAxL9Cpmvq7"
          defaultMessage="Hosier Lane is a living graffiti gallery"
          description="Fun fact headline shown while AI trip generation is in progress for Melbourne (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="0mJcl6uYz2fnUcG-_qRz"
          defaultMessage="Street artists repaint its bluestone walls almost daily, so the artwork is never exactly the same. Official city policy protects the laneway as a legal street-art zone."
          description="Fun fact detail shown while AI trip generation is in progress for Melbourne (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="bsI1CsVkoOa75H32_gmI"
          defaultMessage="Locals joke about “four seasons in one day”"
          description="Fun fact headline shown while AI trip generation is in progress for Melbourne (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="w43o9MqItaDoT9uKynp9"
          defaultMessage="Melbourne’s position between hot inland plains and the cool Southern Ocean can swing temperatures by 10 °C within hours. Always pack both sunscreen and a jacket."
          description="Fun fact detail shown while AI trip generation is in progress for Melbourne (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="TX19-KjrtLwcLiksyVOW"
          defaultMessage="Melbourne Zoo opened in 1862"
          description="Fun fact headline shown while AI trip generation is in progress for Melbourne (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="r0ki8BDnpxDLAyfbedtE"
          defaultMessage="It is the oldest zoo in Australia and now cares for over 300 species. Visitors can stay overnight during special “Roar & Snore” camps."
          description="Fun fact detail shown while AI trip generation is in progress for Melbourne (fact 8)"
        />
      ),
    },
  ],
  Perth: [
    {
      text: (
        <FormattedMessage
          id="LLHZcFzZihaBkfbghiz5"
          defaultMessage="Perth is the sunniest capital city in Australia"
          description="Fun fact headline shown while AI trip generation is in progress for Perth (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="SNg20HkgPGdKja3FCS3q"
          defaultMessage="Meteorologists record an average of 8 hours of sunshine per day across the year. Clear skies mean spectacular Indian Ocean sunsets almost nightly."
          description="Fun fact detail shown while AI trip generation is in progress for Perth (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="9tkx7G81L0VUaLFW2fFw"
          defaultMessage="It is closer to Jakarta than Sydney"
          description="Fun fact headline shown while AI trip generation is in progress for Perth (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="YA2YfGsNzC2OB0yYAsax"
          defaultMessage="Indonesia’s capital lies about 3,000 km away, while Sydney is nearly 3,300 km distant. This west-coast isolation shapes Perth’s laid-back vibe."
          description="Fun fact detail shown while AI trip generation is in progress for Perth (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="1eAwOPcjJ1KNMg6mNH0R"
          defaultMessage="Kings Park is one of the world’s largest inner-city parks"
          description="Fun fact headline shown while AI trip generation is in progress for Perth (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="YJZE3fkqzIo6YwQbgBQ_"
          defaultMessage="Covering 400 ha, it outsizes New York’s Central Park. Hundreds of native wildflowers bloom there each spring."
          description="Fun fact detail shown while AI trip generation is in progress for Perth (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="KYfCPxnBOE8nzD16Bo_I"
          defaultMessage="Perth’s beaches stretch for 19 km"
          description="Fun fact headline shown while AI trip generation is in progress for Perth (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="0umnN_3w9mQ8L7B_mMkj"
          defaultMessage="White-sand favourites like Cottesloe and Scarborough offer easy bus access from the CBD. Afternoon sea-breezes, nicknamed “the Fremantle Doctor”, cool the shoreline."
          description="Fun fact detail shown while AI trip generation is in progress for Perth (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="1xyamhCcVyAh7xIqDxJM"
          defaultMessage="The Swan River is named for its native black swans"
          description="Fun fact headline shown while AI trip generation is in progress for Perth (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="w6Sb630aOzN5VBWRgi2g"
          defaultMessage="These graceful birds feature on Perth’s city flag and state emblem. Kayakers share the river with dolphins that hunt bream near the Narrows Bridge."
          description="Fun fact detail shown while AI trip generation is in progress for Perth (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="rGZFtXG3qRoU1cSKTxqf"
          defaultMessage="Rottnest Island is home to the quokka"
          description="Fun fact headline shown while AI trip generation is in progress for Perth (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="BzYd1XAdGXOOf76cWOMp"
          defaultMessage="These smiling marsupials have made the island an Instagram sensation. Selfies are fine, but feeding wildlife is strictly prohibited."
          description="Fun fact detail shown while AI trip generation is in progress for Perth (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="HB7gFAXNH60LOFODIJrf"
          defaultMessage="Perth has a thriving craft-beer scene"
          description="Fun fact headline shown while AI trip generation is in progress for Perth (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="MQZ4YERt5IcU0Q6ndaKI"
          defaultMessage="More than 60 micro-breweries operate across the metropolitan area. Many pour experimental ales flavoured with native bush ingredients."
          description="Fun fact detail shown while AI trip generation is in progress for Perth (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="OEYyj8bxGZCEE3tOcKCP"
          defaultMessage="The skyline glows under more than 300 sunny days a year"
          description="Fun fact headline shown while AI trip generation is in progress for Perth (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="cz-98SrT88JDOkbSZVK9"
          defaultMessage="Long daylight hours power Perth’s growing solar-panel uptake, which is the highest of any Australian capital."
          description="Fun fact detail shown while AI trip generation is in progress for Perth (fact 8)"
        />
      ),
    },
  ],
  Brisbane: [
    {
      text: (
        <FormattedMessage
          id="3noHiJ1oqPVLlyvUBb7u"
          defaultMessage="The Brisbane River snakes through the city for 76 km"
          description="Fun fact headline shown while AI trip generation is in progress for Brisbane (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="B-CKGBw1G7Ja2N8nLZIE"
          defaultMessage="Locals affectionately call it the 'Brown Snake' because of its winding shape and muddy colour. River-cat ferries and paddle-steamers ply its bends daily."
          description="Fun fact detail shown while AI trip generation is in progress for Brisbane (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="UZV6W34_nJxW5FV2Oylo"
          defaultMessage="The Story Bridge can be climbed"
          description="Fun fact headline shown while AI trip generation is in progress for Brisbane (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="9zPEaX6E4QTZJqUBbRki"
          defaultMessage="Adventure seekers scale its 80-m steel summit for panoramic skyline views. Night climbs offer a glittering perspective of the CBD lights."
          description="Fun fact detail shown while AI trip generation is in progress for Brisbane (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="sca2t35GVmoKCBZH_nr1"
          defaultMessage="Brisbane hosts the world’s largest koala sanctuary"
          description="Fun fact headline shown while AI trip generation is in progress for Brisbane (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="QvLhRSKw1YvgnWBlhJi8"
          defaultMessage="Lone Pine Sanctuary opened in 1927 and now cares for over 130 koalas. Visitors can also meet kangaroos, wombats and platypuses."
          description="Fun fact detail shown while AI trip generation is in progress for Brisbane (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="l0XBa7kNt2rPlbEcBT4r"
          defaultMessage="The city enjoys 283 sunny days a year"
          description="Fun fact headline shown while AI trip generation is in progress for Brisbane (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Y3wNA2ambIyoMKwhtk1m"
          defaultMessage="A humid-subtropical climate encourages an outdoor lifestyle of riverside cycling and rooftop bars. Short, mild winters rarely drop below 10 °C."
          description="Fun fact detail shown while AI trip generation is in progress for Brisbane (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="6QA4jQXFyXfLmBI4s041"
          defaultMessage="South Bank Parklands were once an industrial wharf"
          description="Fun fact headline shown while AI trip generation is in progress for Brisbane (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="-XD9JAH30Lsjkg_K6hmk"
          defaultMessage="Redeveloped for Expo 88, the precinct now houses galleries, a beach lagoon and weekly artisan markets. It is Brisbane’s cultural heart."
          description="Fun fact detail shown while AI trip generation is in progress for Brisbane (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="7cjv35luk3wlEb0QxJEQ"
          defaultMessage="Brisbane is Australia’s third-largest city"
          description="Fun fact headline shown while AI trip generation is in progress for Brisbane (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="wwbOIunnzdBfGSdMuH-f"
          defaultMessage="Despite rapid growth, locals insist it keeps a relaxed, small-town friendliness. Many commuters still greet bus drivers by name."
          description="Fun fact detail shown while AI trip generation is in progress for Brisbane (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="gkvaoExpIUN-AsDHy1oK"
          defaultMessage="The Gabba stadium dates back to 1895"
          description="Fun fact headline shown while AI trip generation is in progress for Brisbane (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="oM3MUcveCYHhDWEzqUkU"
          defaultMessage="It hosts cricket Tests and AFL matches for 42,000 fans. A major rebuild is planned for the 2032 Olympic Games."
          description="Fun fact detail shown while AI trip generation is in progress for Brisbane (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="QRnFaPb-Yko8Lm0w5-pC"
          defaultMessage="Jacaranda season turns the city purple each October"
          description="Fun fact headline shown while AI trip generation is in progress for Brisbane (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="G-f4HCiW62I-VsQTLvbf"
          defaultMessage="University students jokingly call the bloom a reminder to start studying for exams. Fallen petals form carpets along suburban streets."
          description="Fun fact detail shown while AI trip generation is in progress for Brisbane (fact 8)"
        />
      ),
    },
  ],
  Adelaide: [
    {
      text: (
        <FormattedMessage
          id="lQtNisG3lz6cJed1CfPv"
          defaultMessage="Adelaide is nicknamed the “City of Churches”"
          description="Fun fact headline shown while AI trip generation is in progress for Adelaide (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="MbA8cps4s1CseMDDg4m2"
          defaultMessage="Nineteenth-century settlers granted freedom of worship, leading to a skyline of spires. Today festivals and food often overshadow its religious heritage."
          description="Fun fact detail shown while AI trip generation is in progress for Adelaide (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="5ofaDOtz_M9Tthr5-DA3"
          defaultMessage="It was Australia’s first planned city"
          description="Fun fact headline shown while AI trip generation is in progress for Adelaide (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="kAX3xjxWTj-xGFVUcRjg"
          defaultMessage="Colonel William Light designed the 1837 grid with wide boulevards and surrounding parklands. His plan still shapes traffic flow and leafy vistas."
          description="Fun fact detail shown while AI trip generation is in progress for Adelaide (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="XVE-phi3NlY638pShsfG"
          defaultMessage="Adelaide hosts one of the world’s biggest arts festivals"
          description="Fun fact headline shown while AI trip generation is in progress for Adelaide (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Sccj1x_6cv7QiMbwxhx_"
          defaultMessage="The Adelaide Fringe welcomes over 7,000 artists each February–March. Pop-up venues transform laneways into buzzing performance spaces."
          description="Fun fact detail shown while AI trip generation is in progress for Adelaide (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="F6U6KcRqffcyAQeYwNMu"
          defaultMessage="More than 900 ha of green space encircle the CBD"
          description="Fun fact headline shown while AI trip generation is in progress for Adelaide (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="t9cBQ17ETlBCqzBbwwSf"
          defaultMessage="The Park Lands are heritage-listed and remain public commons for sport, picnics and native-plant conservation."
          description="Fun fact detail shown while AI trip generation is in progress for Adelaide (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="H0h_q0PQM9rvQDl1vl4S"
          defaultMessage="Three major wine regions sit within an hour’s drive"
          description="Fun fact headline shown while AI trip generation is in progress for Adelaide (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="X2vjhiIbx7lepqSuu2X0"
          defaultMessage="The Barossa, McLaren Vale and Adelaide Hills lure day-trippers with cellar doors and shiraz tastings. BYO designated driver is highly advised."
          description="Fun fact detail shown while AI trip generation is in progress for Adelaide (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="piKyeMWgvrH7Iv2fAIPt"
          defaultMessage="The South Australian Museum holds the world’s largest display of Aboriginal artefacts"
          description="Fun fact headline shown while AI trip generation is in progress for Adelaide (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="01UBoFmddHnxcYm086FY"
          defaultMessage="Over 30,000 cultural items showcase First Nations history from across the continent. Many pieces are displayed in consultation with traditional owners."
          description="Fun fact detail shown while AI trip generation is in progress for Adelaide (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="6E-u0Wg7z92mGtCe_uKQ"
          defaultMessage="Adelaide Oval has hosted cricket since 1871"
          description="Fun fact headline shown while AI trip generation is in progress for Adelaide (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="HBFL6pH3l-8NsMWtgDwe"
          defaultMessage="Its grassy northern hill and heritage scoreboard are beloved quirks amid modern grandstands. The footbridge links fans directly to the city centre."
          description="Fun fact detail shown while AI trip generation is in progress for Adelaide (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="qcvcXcIZizDtAbVcLwOF"
          defaultMessage="Haigh’s Chocolates was founded here in 1915"
          description="Fun fact headline shown while AI trip generation is in progress for Adelaide (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="KZsb_P959wn60AaX1aLs"
          defaultMessage="Australia’s oldest family-owned chocolate maker still roasts its own cocoa beans on site. Free factory tours hand out generous samples."
          description="Fun fact detail shown while AI trip generation is in progress for Adelaide (fact 8)"
        />
      ),
    },
  ],
  Darwin: [
    {
      text: (
        <FormattedMessage
          id="a1XiVqzMYjyTXU-C9uQf"
          defaultMessage="Darwin is closer to Bali than Sydney"
          description="Fun fact headline shown while AI trip generation is in progress for Darwin (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="8ocpurEoZYfXvTEZc2di"
          defaultMessage="The Indonesian island lies just over 2½ hours away by plane, while Sydney is a four-hour flight. Its tropical proximity shapes cuisine rich in Asian flavours."
          description="Fun fact detail shown while AI trip generation is in progress for Darwin (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="hWe65WPrZEBUNlg2qZ7w"
          defaultMessage="Cyclone Tracy nearly destroyed the city in 1974"
          description="Fun fact headline shown while AI trip generation is in progress for Darwin (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="UDBLM1c9_PyiqgG9GWEX"
          defaultMessage="Winds stripped 70 % of buildings on Christmas Day, prompting Australia’s largest peacetime evacuation. Much of modern Darwin was rebuilt to new cyclone-proof standards."
          description="Fun fact detail shown while AI trip generation is in progress for Darwin (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="ycHKr497Rz0h5Wrt38zX"
          defaultMessage="The city has two distinct seasons"
          description="Fun fact headline shown while AI trip generation is in progress for Darwin (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="bnl_tvf5Nibb-0NOd1j0"
          defaultMessage="The Dry runs from May to October with blue skies and 30 °C days, while the Wet brings dramatic monsoon storms. Locals celebrate the first rains with open-air showers."
          description="Fun fact detail shown while AI trip generation is in progress for Darwin (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="x2GUd8P7ZFSyPH1jjBOB"
          defaultMessage="Mindil Beach Sunset Market is a Top-End icon"
          description="Fun fact headline shown while AI trip generation is in progress for Darwin (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="xWJsnpqHxxNKw1pWqwFz"
          defaultMessage="Every dry-season evening, food stalls, fire twirlers and buskers line the sand dunes. Satay skewers and mango smoothies are perennial favourites."
          description="Fun fact detail shown while AI trip generation is in progress for Darwin (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="QwZKZYOjL8K0nJjeZQ3c"
          defaultMessage="Saltwater crocodiles rule the waterways"
          description="Fun fact headline shown while AI trip generation is in progress for Darwin (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="OA2HV7zjdyj4Z4U_5nU8"
          defaultMessage="Tours on the Adelaide River showcase 5-m giants leaping for buffalo meat. The nearby “Cage of Death” lets brave visitors float beside a croc behind acrylic walls."
          description="Fun fact detail shown while AI trip generation is in progress for Darwin (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="qgR9tL6sN34GylUluoje"
          defaultMessage="Darwin played a key role in WWII"
          description="Fun fact headline shown while AI trip generation is in progress for Darwin (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="_5Eo0ZptrPni0gjl8Q_6"
          defaultMessage="It endured 64 Japanese air raids in 1942—more bombs than fell on Pearl Harbor. Museum exhibits include a salvaged Spitfire and wharf wreckage."
          description="Fun fact detail shown while AI trip generation is in progress for Darwin (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="m00WmJol7Qfkdt6mHmiR"
          defaultMessage="The Museum and Art Gallery houses “Sweetheart”"
          description="Fun fact headline shown while AI trip generation is in progress for Darwin (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="tKxLhcO8TG9DzalmYlGH"
          defaultMessage="This 5.1-m crocodile was notorious for attacking outboard motors in the 1970s. It was accidentally drowned after becoming entangled in a trap."
          description="Fun fact detail shown while AI trip generation is in progress for Darwin (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="SQdSo8u8ykQAw1vDCFf6"
          defaultMessage="Darwin Harbour is five times bigger than Sydney Harbour"
          description="Fun fact headline shown while AI trip generation is in progress for Darwin (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="h1j3jz7IgyxFXtbVHuHe"
          defaultMessage="Mangroves fringe its vast, high-tidal estuary that supports dugongs, turtles and dolphins. Sunset cruises depart daily from the Waterfront precinct."
          description="Fun fact detail shown while AI trip generation is in progress for Darwin (fact 8)"
        />
      ),
    },
  ],
  'Gold Coast': [
    {
      text: (
        <FormattedMessage
          id="Qf4SOz7XylkTx0hbpFg4"
          defaultMessage="The Gold Coast has over 70 km of coastline"
          description="Fun fact headline shown while AI trip generation is in progress for Gold Coast (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="CleRIcwmVxSMTaeblutj"
          defaultMessage="It’s home to icons like Surfers Paradise, Burleigh Heads and Snapper Rocks. World-class surf breaks here host annual Quiksilver Pro contests."
          description="Fun fact detail shown while AI trip generation is in progress for Gold Coast (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="kUQtUTm_1Ke-ocJtZDuQ"
          defaultMessage="Surfers Paradise was originally called Elston"
          description="Fun fact headline shown while AI trip generation is in progress for Gold Coast (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="CqLbfkBo8Yx1IxhlgBRr"
          defaultMessage="The 1933 name-change was a publicity master-stroke that lured holidaymakers south from Brisbane. High-rise hotels quickly followed, defining the skyline we know today."
          description="Fun fact detail shown while AI trip generation is in progress for Gold Coast (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="pJAdlS9-eKPBXVV6V0I_"
          defaultMessage="The Gold Coast has more canals than Venice"
          description="Fun fact headline shown while AI trip generation is in progress for Gold Coast (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="EZ5bYHwQjbwz4iSYOm8L"
          defaultMessage="About 400 km of man-made waterways weave past homes with private pontoons. Water taxis and recreational cruisers link restaurants straight to the back door."
          description="Fun fact detail shown while AI trip generation is in progress for Gold Coast (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="33DhRs52Z-tMnZDn_H8P"
          defaultMessage="The Q1 building is one of the tallest residential towers in the world"
          description="Fun fact headline shown while AI trip generation is in progress for Gold Coast (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Ja_ZfE_kUCOr4aGi25Ww"
          defaultMessage="Rising 322 m, it pierces the subtropical skyline like a silver needle. An express lift whooshes to the SkyPoint deck in just 42 seconds."
          description="Fun fact detail shown while AI trip generation is in progress for Gold Coast (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="k47P06vf3Vh2P-QQOGsO"
          defaultMessage="The Gold Coast hosts over 12 million visitors annually"
          description="Fun fact headline shown while AI trip generation is in progress for Gold Coast (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="42069SQmL_Z2_LzBWnOA"
          defaultMessage="Schoolies Week alone draws tens of thousands of graduating students each November. Tourism joins film production and education as pillars of the local economy."
          description="Fun fact detail shown while AI trip generation is in progress for Gold Coast (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Xe4i_gVfK2T6eUxqH27J"
          defaultMessage="Burleigh Head National Park offers rainforest trails just minutes from the beach"
          description="Fun fact headline shown while AI trip generation is in progress for Gold Coast (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="y-szJhtSJTuo9KJ2266i"
          defaultMessage="Wild turkeys and water dragons scuttle under pandanus as the track climbs to ocean lookouts. Humpback whales are often spotted passing the headland from May to October."
          description="Fun fact detail shown while AI trip generation is in progress for Gold Coast (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="bLsU2q19ar5DL-Mc_bsH"
          defaultMessage="The area has a booming arts and culture scene"
          description="Fun fact headline shown while AI trip generation is in progress for Gold Coast (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="EsDlflDMM_IIrZsij_i2"
          defaultMessage="HOTA (Home of the Arts) includes galleries, a lakeside amphitheatre and Australia’s tallest public art tower. Outdoor concerts there seat up to 5,000 stargazers."
          description="Fun fact detail shown while AI trip generation is in progress for Gold Coast (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="ZfnoKLyZFT7ELeXHQMea"
          defaultMessage="The Gold Coast was a key filming location for major Hollywood movies"
          description="Fun fact headline shown while AI trip generation is in progress for Gold Coast (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Y46cq8gMUyDVE4K9bBUn"
          defaultMessage="Village Roadshow Studios’ eight sound stages hosted blockbusters like *Aquaman* and *Thor: Ragnarok*. Local beaches often double for far-flung tropical worlds."
          description="Fun fact detail shown while AI trip generation is in progress for Gold Coast (fact 8)"
        />
      ),
    },
  ],
  Katoomba: [
    {
      text: (
        <FormattedMessage
          id="ljpa-Xoe_3xmzzKNTsoX"
          defaultMessage="Katoomba is home to the world’s steepest passenger railway"
          description="Fun fact headline shown while AI trip generation is in progress for Katoomba (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="xIQIjH6z3eP5QyF7lHdT"
          defaultMessage="The Scenic Railway plunges into the Jamison Valley at a 52° pitch. Originally built for coal hauling, it now thrills tourists with glass-roof carriages."
          description="Fun fact detail shown while AI trip generation is in progress for Katoomba (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="So4BYwvzdnVBcU9rg67N"
          defaultMessage="The name 'Katoomba' means 'shining falling water'"
          description="Fun fact headline shown while AI trip generation is in progress for Katoomba (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="sUFj9boD_6fMjFINfr6n"
          defaultMessage="Derived from the Gundungurra word 'Kedumba', it honours the nearby cascades of Katoomba Falls. Indigenous rock art and lore permeate the surrounding cliffs."
          description="Fun fact detail shown while AI trip generation is in progress for Katoomba (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="PwN-YBksbCmf5RNXKi4o"
          defaultMessage="Echo Point welcomes over two million visitors a year"
          description="Fun fact headline shown while AI trip generation is in progress for Katoomba (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="qg_wYj3Uq4vVbMqX8fz6"
          defaultMessage="They come to photograph the Three Sisters rock pillars and sweeping eucalyptus haze. Sunrise turns the sandstone a vivid orange."
          description="Fun fact detail shown while AI trip generation is in progress for Katoomba (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="qdgcszkstc6QoOeB_nH1"
          defaultMessage="Katoomba’s crisp “champagne air” lured 1900s health retreats"
          description="Fun fact headline shown while AI trip generation is in progress for Katoomba (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="KUoZ9b6k0QPXp_GrCH3B"
          defaultMessage="Grand guest-houses promised a tonic escape from Sydney smog. Many heritage-listed buildings still line the main street."
          description="Fun fact detail shown while AI trip generation is in progress for Katoomba (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="dfsmKHTWc4C766BtKvnw"
          defaultMessage="The Scenic Skyway glides 270 m above rainforest"
          description="Fun fact headline shown while AI trip generation is in progress for Katoomba (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="1VAqFixcXEZLKssrTQ1b"
          defaultMessage="Its electro-chromic floor clouds over, then clears to reveal dizzying views straight down. On clear days you can spot Mount Solitary far across the valley."
          description="Fun fact detail shown while AI trip generation is in progress for Katoomba (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="ySl3X1IHvZDZXM0UGCit"
          defaultMessage="The Winter Magic Festival paints the town rainbow every June"
          description="Fun fact headline shown while AI trip generation is in progress for Katoomba (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="PSde9MSRPhBAVcM4kEX1"
          defaultMessage="Locals don costumes ranging from steampunk to fairies for a solstice street parade. Performers fill cafes with folk music and poetry slams."
          description="Fun fact detail shown while AI trip generation is in progress for Katoomba (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="IISPO-_qCNV9KDdxW6ZW"
          defaultMessage="Katoomba Street sits about 1,017 m above sea level"
          description="Fun fact headline shown while AI trip generation is in progress for Katoomba (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ORD44O1tl6DS2x0CZl3Y"
          defaultMessage="That height brings cool summers and occasional snowfall in July. It is one of the highest main streets in Australia."
          description="Fun fact detail shown while AI trip generation is in progress for Katoomba (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="hrrs20OD5i6G7PEwq8HF"
          defaultMessage="Coal once powered Katoomba’s tourism boom"
          description="Fun fact headline shown while AI trip generation is in progress for Katoomba (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ERIzKTlydTu9Qf_hIbzR"
          defaultMessage="A 19th-century incline railway hauled shale to the valley floor. The repurposed tracks now whisk hikers back up the escarpment."
          description="Fun fact detail shown while AI trip generation is in progress for Katoomba (fact 8)"
        />
      ),
    },
  ],
  'Hayman Island': [
    {
      text: (
        <FormattedMessage
          id="9LviO6k33tI3iKo-egzn"
          defaultMessage="Hayman is the northernmost of the Whitsunday Islands"
          description="Fun fact headline shown while AI trip generation is in progress for Hayman Island (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="OX_Dw9ze9UbdRLwfVYd7"
          defaultMessage="It lies just 33 km off Queensland’s coast on the Great Barrier Reef. Warm currents keep the surrounding coral gardens vibrant year-round."
          description="Fun fact detail shown while AI trip generation is in progress for Hayman Island (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="R1ySssIjD9kBEY34A83L"
          defaultMessage="The entire island operates as a single luxury resort"
          description="Fun fact headline shown while AI trip generation is in progress for Hayman Island (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="5866w6y4S2VlG4PlQ_Ri"
          defaultMessage="Guests have exclusive run of 400 ha of rainforest, reef and beaches. Environmental officers oversee strict turtle-nesting and reef-conservation programs."
          description="Fun fact detail shown while AI trip generation is in progress for Hayman Island (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="MarZ62uiMYJgP3DNjPNB"
          defaultMessage="Hayman’s hexagonal pool holds seven Olympic pools’ worth of water"
          description="Fun fact headline shown while AI trip generation is in progress for Hayman Island (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="5JVV2Rw0YulNrhkCEtes"
          defaultMessage="Covering 7,300 m², it is one of the largest resort lagoons in the Southern Hemisphere. A sand-fringed inlet creates the illusion of a private beach inside the pool."
          description="Fun fact detail shown while AI trip generation is in progress for Hayman Island (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="iLxoXrce-K1QW_xqLtvO"
          defaultMessage="Sir Reginald Ansett opened the first hotel here in 1950"
          description="Fun fact headline shown while AI trip generation is in progress for Hayman Island (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="M8jPbnRSTDdhBoXQaBpH"
          defaultMessage="The aviation pioneer used his seaplanes to ferry wealthy guests direct to paradise. Subsequent owners have continually re-imagined Hayman in six-star style."
          description="Fun fact detail shown while AI trip generation is in progress for Hayman Island (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="gbd3lbMGl2lvUiBz1qG8"
          defaultMessage="Green sea turtles nest on Hayman’s beaches"
          description="Fun fact headline shown while AI trip generation is in progress for Hayman Island (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="awZMrsfUgYkFlkC71peh"
          defaultMessage="Hatchlings scramble to sea between November and March under ranger protection. Guests can join nightly walks to monitor emerging clutches."
          description="Fun fact detail shown while AI trip generation is in progress for Hayman Island (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="68yGNFqSnswQ9U_OhNLI"
          defaultMessage="Arrivals are by helicopter, seaplane or luxury launch"
          description="Fun fact headline shown while AI trip generation is in progress for Hayman Island (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="4f91AMK7HyjWHjiuHC52"
          defaultMessage="The 15-minute hop from Hamilton Island offers aerial views of Heart Reef. Those prone to seasickness can even book a private charter jet."
          description="Fun fact detail shown while AI trip generation is in progress for Hayman Island (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="ExbnANn_ozwrLlLFHVqw"
          defaultMessage="Hayman sits within a UNESCO World Heritage area"
          description="Fun fact headline shown while AI trip generation is in progress for Hayman Island (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="2Z9DQl8gcVvw_PRxl_oc"
          defaultMessage="More than 1,500 fish species and 600 coral types thrive in its surrounding marine park. Divers regularly spot manta rays cruising the bommies."
          description="Fun fact detail shown while AI trip generation is in progress for Hayman Island (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="rU_EP7mmp9TYr74VDEEJ"
          defaultMessage="The Beatles holidayed here in 1964"
          description="Fun fact headline shown while AI trip generation is in progress for Hayman Island (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="y9409PeKt7AZQldP6UHh"
          defaultMessage="They arrived incognito by boat for a rare mid-tour break. Memorabilia from the visit still hangs in the resort’s bar."
          description="Fun fact detail shown while AI trip generation is in progress for Hayman Island (fact 8)"
        />
      ),
    },
  ],
  Bali: [
    {
      text: (
        <FormattedMessage
          id="cbssxbiNfRQ0ZaqTVcED"
          defaultMessage="Bali is nicknamed the 'Island of the Gods'"
          description="Fun fact headline shown while AI trip generation is in progress for Bali (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="HFjvMo67uSTFhNWCZJTp"
          defaultMessage="More than 20,000 Hindu temples dot its volcanic landscape, each linked to local guardian spirits. Offerings of flowers and incense line streets, hotels and even motorbikes every single day."
          description="Fun fact detail shown while AI trip generation is in progress for Bali (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="tD0ttx490Ic3EjcCRP1z"
          defaultMessage="It’s Indonesia’s only Hindu-majority province"
          description="Fun fact headline shown while AI trip generation is in progress for Bali (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="boVNvtvAG5WmiKIBbH_5"
          defaultMessage="Over 80 % of Balinese practise a unique blend of Hinduism and ancestral animism. Rituals follow both the Sanskrit calendar and older animist cycles."
          description="Fun fact detail shown while AI trip generation is in progress for Bali (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="9EXPdk44TRDPerfkEn8t"
          defaultMessage="Nyepi Day plunges the island into 24 hours of silence"
          description="Fun fact headline shown while AI trip generation is in progress for Bali (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="8-UtTjIrKhQkCpVfp9MF"
          defaultMessage="Airports close, lights dim and even beaches fall quiet for the Saka New Year. Locals use the day to meditate, while tourists enjoy star-filled night skies free of light pollution."
          description="Fun fact detail shown while AI trip generation is in progress for Bali (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="z7Xwj2zMONPrHTtJ4lQc"
          defaultMessage="Mount Agung dominates at 3,031 m"
          description="Fun fact headline shown while AI trip generation is in progress for Bali (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="yEZtiprGDEw_vdOVm3b-"
          defaultMessage="Balinese believe this active volcano is the earthly home of the gods. Pilgrims trek pre-dawn to Pura Besakih on its slopes for full-moon ceremonies."
          description="Fun fact detail shown while AI trip generation is in progress for Bali (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="EttSKmDgModybIT25YtV"
          defaultMessage="Bali’s Subak rice terraces form a 1,000-year-old UNESCO irrigation system"
          description="Fun fact headline shown while AI trip generation is in progress for Bali (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="BY7V0srKf4Hcd-IhNqVn"
          defaultMessage="The co-operative water-sharing network reflects the Tri Hita Karana philosophy of harmony among people, nature and spirit. Farmers still meet at temples to set planting dates together."
          description="Fun fact detail shown while AI trip generation is in progress for Bali (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="7E6HSMyrQiZWiDt28Ce9"
          defaultMessage="Some eastern beaches shimmer black"
          description="Fun fact headline shown while AI trip generation is in progress for Bali (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="JG2W40lnm1brs8sHBZCR"
          defaultMessage="Volcanic minerals from Mount Agung tint the sand a dramatic charcoal hue. Photographers flock here at sunrise for mirrored reflections of the fiery skyline."
          description="Fun fact detail shown while AI trip generation is in progress for Bali (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="_gjofWN2cjYSFQwjhRrH"
          defaultMessage="Kopi luwak—the world’s priciest coffee—hails from Bali"
          description="Fun fact headline shown while AI trip generation is in progress for Bali (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="l5DfcKJEaFVxzyr8L3I0"
          defaultMessage="Wild civets digest ripe arabica cherries, fermenting the beans before roasting. Ethical farms now focus on free-roaming animals rather than caged production."
          description="Fun fact detail shown while AI trip generation is in progress for Bali (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="nR0aBEYd9hU_E4JYxvI3"
          defaultMessage="A Balinese calendar cycle lasts just 210 days"
          description="Fun fact headline shown while AI trip generation is in progress for Bali (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="PKXHqUDq7gs-VuPdgfO0"
          defaultMessage="Known as the Pawukon system, it overlays ten concurrent week-lengths that guide festivals and temple anniversaries. Many families celebrate two birthdays each “western” year as a result."
          description="Fun fact detail shown while AI trip generation is in progress for Bali (fact 8)"
        />
      ),
    },
  ],
  'Nusa Dua': [
    {
      text: (
        <FormattedMessage
          id="ZS-JSJKVZ8DFVHoBUIeO"
          defaultMessage="'Nusa Dua' means 'two islands'"
          description="Fun fact headline shown while AI trip generation is in progress for Nusa Dua (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="L7zj8YZ4WjIvKzPrhb8W"
          defaultMessage="It refers to the twin islets just off the Bukit Peninsula that shelter the main lagoon. At low tide you can walk across to one of them for sunrise photos."
          description="Fun fact detail shown while AI trip generation is in progress for Nusa Dua (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="tx-9EjkRecN_v8zsLik3"
          defaultMessage="Nusa Dua was built from scratch in the 1970s"
          description="Fun fact headline shown while AI trip generation is in progress for Nusa Dua (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="rBpOJT4WaDhq5slqYMjy"
          defaultMessage="The Bali Tourism Development Corporation reclaimed land to create Indonesia’s first master-planned resort enclave. Utilities run underground to keep palm-lined avenues uncluttered."
          description="Fun fact detail shown while AI trip generation is in progress for Nusa Dua (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="3flqRKXvuJ4BVd979nNm"
          defaultMessage="Nusa Dua hosts one of Indonesia’s largest convention centres"
          description="Fun fact headline shown while AI trip generation is in progress for Nusa Dua (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ZzVD_k2LmWwo-XYnypOX"
          defaultMessage="The Bali International Convention Centre can seat more than 6,000 delegates. World leaders from APEC to the G20 have gathered under its soaring Balinese-roofed halls."
          description="Fun fact detail shown while AI trip generation is in progress for Nusa Dua (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="BDx5YPiyVlFpKbcEFRlb"
          defaultMessage="The Pasifika Museum holds the world’s largest collection of Pacific and Asia-Pacific art"
          description="Fun fact headline shown while AI trip generation is in progress for Nusa Dua (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="LPSrNsOyI17IKOwiKNkV"
          defaultMessage="Over 2,500 works range from NZ Māori carvings to Gauguin’s Tahiti paintings. Air-conditioned galleries provide a cool cultural break from the beach."
          description="Fun fact detail shown while AI trip generation is in progress for Nusa Dua (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="iN4L3H3Jz9D5JDVsgNlP"
          defaultMessage="The 'Water Blow' at Tanjung Benoa sends waves 10 m into the air"
          description="Fun fact headline shown while AI trip generation is in progress for Nusa Dua (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Z1mJ8WjaFmPCkRvgfpYv"
          defaultMessage="A narrow sea cave funnels surf into an explosive geyser—spectacular at high tide. Viewing decks keep spectators safe from the spray."
          description="Fun fact detail shown while AI trip generation is in progress for Nusa Dua (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="VnYvugXI7EtP1iXS3ad-"
          defaultMessage="The Bali Collection is Nusa Dua’s open-air shopping and dining complex"
          description="Fun fact headline shown while AI trip generation is in progress for Nusa Dua (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="bXDL6PQmCP8KLD46f3Ut"
          defaultMessage="More than 30,000 m² house boutiques, art galleries and nightly live music. A free shuttle loops the resort zone every 15 minutes."
          description="Fun fact detail shown while AI trip generation is in progress for Nusa Dua (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="XDZm6KhXpG0ounE-egB8"
          defaultMessage="Turtle Island (Pulau Serangan) is just 5 km away"
          description="Fun fact headline shown while AI trip generation is in progress for Nusa Dua (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="tQqDJD-YYW2zM8EXX6y_"
          defaultMessage="The conservation centre rescues injured green and hawksbill turtles before releasing them. Visitors can help weigh hatchlings for health records."
          description="Fun fact detail shown while AI trip generation is in progress for Nusa Dua (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="NP9AdBiLunLbGyM-re0Z"
          defaultMessage="Geger Beach is home to an ancient Balinese temple"
          description="Fun fact headline shown while AI trip generation is in progress for Nusa Dua (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="P6F8lZe9g9NbSzLsmm09"
          defaultMessage="Pura Geger crowns a headland overlooking calm turquoise waters ideal for SUP boarding. Local seaweed farmers still harvest edible algae from the shallows at dawn."
          description="Fun fact detail shown while AI trip generation is in progress for Nusa Dua (fact 8)"
        />
      ),
    },
  ],

  Seminyak: [
    {
      text: (
        <FormattedMessage
          id="l8XJEJb4H-JTZ7oHMDwH"
          defaultMessage="Seminyak’s beach is famous for its colourful beanbags and umbrellas"
          description="Fun fact headline shown while AI trip generation is in progress for Seminyak (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="h95U3VjVx67q4xeJhkIV"
          defaultMessage="Laid-back bars set them out each afternoon, turning the shoreline into a rainbow lounge. Sunset DJs spin chilled house beats while vendors wander with grilled corn."
          description="Fun fact detail shown while AI trip generation is in progress for Seminyak (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Pxmgq-0DyaW9UWCfXHtd"
          defaultMessage="'Seminyak' comes from the Balinese words for 'reef' and 'harbour'"
          description="Fun fact headline shown while AI trip generation is in progress for Seminyak (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="4SusHEDXeIc8kYMt_UEr"
          defaultMessage="What was once a quiet fishing stretch morphed into a luxury enclave in the 2000s. Many old jukung boats now serve as quirky beach-bar decor."
          description="Fun fact detail shown while AI trip generation is in progress for Seminyak (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="GmpKRvxwI3gAxbq6OFCW"
          defaultMessage="Seminyak hosts over 500 private villas—one of the highest luxury-villa densities in Bali"
          description="Fun fact headline shown while AI trip generation is in progress for Seminyak (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="397n3xD4WqgGsRKff6nn"
          defaultMessage="Most come with plunge pools, open-air bathrooms and butler service. Digital nomads often rent these long-term for a taste of Balinese indoor-outdoor living."
          description="Fun fact detail shown while AI trip generation is in progress for Seminyak (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="PQ-V37XlqOv5mWlw6dcy"
          defaultMessage="Petitenget Temple dates back to the 6th century"
          description="Fun fact headline shown while AI trip generation is in progress for Seminyak (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="waXqRjsRbiEg_M-NRlM2"
          defaultMessage="Locals believe it guards the coast against mischievous sea spirits. Colourful odalan festivals fill its courtyard with dance, gamelan and incense every 210 days."
          description="Fun fact detail shown while AI trip generation is in progress for Seminyak (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="2YItAfJrGkRUoe5VYV5A"
          defaultMessage="Potato Head’s façade is built from 6,000 reclaimed wooden shutters"
          description="Fun fact headline shown while AI trip generation is in progress for Seminyak (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="TCrH4xpaXvP24B-Tu3qe"
          defaultMessage="Collected from all over Indonesia, the multicoloured panels wrap an amphitheatre-shaped beach club. Sustainability workshops inside teach guests about up-cycling."
          description="Fun fact detail shown while AI trip generation is in progress for Seminyak (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="aRYNQh5ol47mQervylEo"
          defaultMessage="Jalan Kayu Aya is home to the Seminyak Flea Market"
          description="Fun fact headline shown while AI trip generation is in progress for Seminyak (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="FstjXKAFttw--gZiHSrE"
          defaultMessage="Over 60 stalls sell everything from rattan handbags to palm-leaf art. Haggling is expected, so start at half the first price and meet in the middle."
          description="Fun fact detail shown while AI trip generation is in progress for Seminyak (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="oj4WFIr-8SqBqaf_ZItR"
          defaultMessage="There’s a “secret” rooftop garden at the Seminyak Village mall"
          description="Fun fact headline shown while AI trip generation is in progress for Seminyak (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="j_k94r_TigY_ahYY_s5R"
          defaultMessage="Few shoppers realise an open-air terrace sits above the boutiques, offering rare ocean views. Yoga classes and craft workshops pop up there on weekends."
          description="Fun fact detail shown while AI trip generation is in progress for Seminyak (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="G5LlJENJ4-VudHiXoEyr"
          defaultMessage="Double Six Beach was once a refuelling point for Japanese seaplanes in WWII"
          description="Fun fact headline shown while AI trip generation is in progress for Seminyak (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="vaGDaqF9xH9AEgq63_jA"
          defaultMessage="Concrete moorings still lurk beneath the sand at low tide. Today the beach is a mellow dawn-patrol spot for long-boarders."
          description="Fun fact detail shown while AI trip generation is in progress for Seminyak (fact 8)"
        />
      ),
    },
  ],
  Canggu: [
    {
      text: (
        <FormattedMessage
          id="mz1Vj1OYG8OsufYGmPBq"
          defaultMessage="Echo Beach’s sand is black"
          description="Fun fact headline shown while AI trip generation is in progress for Canggu (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="9YuVYFvu7icQntbjD2JF"
          defaultMessage="Volcanic minerals from Mount Agung lend it a dramatic, glittering hue. Sunset bonfire gatherings give the shoreline an otherworldly glow."
          description="Fun fact detail shown while AI trip generation is in progress for Canggu (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="4LmI6tpzMP47TilofVMX"
          defaultMessage="Tanah Lot Temple sits on a rocky islet just 15 km from Canggu"
          description="Fun fact headline shown while AI trip generation is in progress for Canggu (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ewgYOmA8OqMt87g0FIbX"
          defaultMessage="It’s one of Bali’s most photographed sunset viewpoints. At high tide waves isolate the shrine, enhancing its mystical aura."
          description="Fun fact detail shown while AI trip generation is in progress for Canggu (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="nZHKR8rp7Vrtckam5fcb"
          defaultMessage="Berawa Beach offers waves up to 3 m high"
          description="Fun fact headline shown while AI trip generation is in progress for Canggu (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="n9hXFqaMdUdVwAMKt0XW"
          defaultMessage="That makes it an intermediate-to-advanced surfer’s playground. Beach breaks here are gentler than Uluwatu but punchier than Kuta."
          description="Fun fact detail shown while AI trip generation is in progress for Canggu (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="gAt4qJANBB5zJGYwy3h0"
          defaultMessage="Canggu was a sleepy rice-farming village until the late 2000s"
          description="Fun fact headline shown while AI trip generation is in progress for Canggu (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="SkFnrcBcBS6Lxgf3yhkQ"
          defaultMessage="Now Instagram cafés, yoga shalas and co-working lofts dot the paddies. Many rice terraces remain in production beside designer villas."
          description="Fun fact detail shown while AI trip generation is in progress for Canggu (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="aW_LA4J0ItoqDmbxRDa3"
          defaultMessage="The Love Anchor Market rotates local artisans every weekend"
          description="Fun fact headline shown while AI trip generation is in progress for Canggu (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="MikFUNsWILxOjNrAkG1X"
          defaultMessage="Hand-made jewellery, home décor and gluten-free doughnuts share bamboo stalls. Live acoustic sets keep the Sunday vibe mellow."
          description="Fun fact detail shown while AI trip generation is in progress for Canggu (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="EzrlOHyz7AMiXSJNJ0c9"
          defaultMessage="Canggu Circle Mall features Bali’s first curved digital-media façade"
          description="Fun fact headline shown while AI trip generation is in progress for Canggu (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="wM6sPypo8Fv4swPO43BN"
          defaultMessage="At night LEDs pulse with moving art and event promos. The architecture won a regional lighting-design award in 2023."
          description="Fun fact detail shown while AI trip generation is in progress for Canggu (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="vf2FyUim5P8X9JvlAYIe"
          defaultMessage="Deus Ex Machina began here as a motorcycle workshop"
          description="Fun fact headline shown while AI trip generation is in progress for Canggu (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="0snsaE_bDxxQCc-4S3CP"
          defaultMessage="It evolved into a cult lifestyle brand fusing surf, art and specialty coffee. Their monthly Sunday Sessions showcase indie bands in the back yard."
          description="Fun fact detail shown while AI trip generation is in progress for Canggu (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="XHWmg2oMvzV1-ZCK316K"
          defaultMessage="Pererenan Beach’s boardwalk is known as Bali’s best “Sunset Point”"
          description="Fun fact headline shown while AI trip generation is in progress for Canggu (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Yyq5sy5GK2xcvdCfhYJQ"
          defaultMessage="The timber path lets you stroll above crashing waves without sand in your shoes. Photographers gather early for silhouetted horse-ride shots."
          description="Fun fact detail shown while AI trip generation is in progress for Canggu (fact 8)"
        />
      ),
    },
  ],
  Ubud: [
    {
      text: (
        <FormattedMessage
          id="A0w2M8AtpgA7fCs5q72T"
          defaultMessage="'Ubud' comes from the Balinese word for medicine"
          description="Fun fact headline shown while AI trip generation is in progress for Ubud (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="JBMkciGUeS51uz6xCfTV"
          defaultMessage="The area was once famed for its healing herbs and traditional 'balian' doctors. Wellness retreats and herbal apothecaries still trade on that centuries-old reputation today."
          description="Fun fact detail shown while AI trip generation is in progress for Ubud (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="zj7rLAJqpfTsIAQhhXHz"
          defaultMessage="The Sacred Monkey Forest Sanctuary is home to around 700 long-tailed macaques"
          description="Fun fact headline shown while AI trip generation is in progress for Ubud (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="U6JbKUGTvGiy1y3YDx_S"
          defaultMessage="Three 14th-century temples hide beneath giant banyan trees inside the reserve. Visitors are advised to stash sunglasses—these cheeky residents are notorious souvenir thieves."
          description="Fun fact detail shown while AI trip generation is in progress for Ubud (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="1UwDluV1yEvtq9H9edRw"
          defaultMessage="Puri Saren Royal Palace was built in the early 1800s"
          description="Fun fact headline shown while AI trip generation is in progress for Ubud (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="k7-CWyeORsG7KvyEbu_s"
          defaultMessage="Members of the Ubud royal family still reside in part of the compound. Nightly legong dance performances light the courtyard with flickering oil lamps."
          description="Fun fact detail shown while AI trip generation is in progress for Ubud (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="cv-jT0Vzs6NvxDaec0Qz"
          defaultMessage="Taman Saraswati (the Water Palace) features a floating lotus-pond stage"
          description="Fun fact headline shown while AI trip generation is in progress for Ubud (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Q0_HcdmbPROeeHUot0lC"
          defaultMessage="Completed in 1951, it honours Saraswati, the Hindu goddess of learning. Evenings see kecak and gamelan shows echo across thousands of blooming pink lotuses."
          description="Fun fact detail shown while AI trip generation is in progress for Ubud (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="e3eKs1I7Eapr73H3q8kZ"
          defaultMessage="The terraced rice fields around Ubud are part of Bali’s UNESCO-listed Subak irrigation system"
          description="Fun fact headline shown while AI trip generation is in progress for Ubud (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Uopy_HaiMBU1zy79seOK"
          defaultMessage="Carved by hand over a thousand years, the terraces illustrate communal water sharing rooted in the Tri Hita Karana philosophy. Dawn treks reveal farmers chanting mantras as they set bamboo sluices."
          description="Fun fact detail shown while AI trip generation is in progress for Ubud (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="gD6PebIzOO454bFF_IjW"
          defaultMessage="Goa Gajah (“Elephant Cave”) is an archaeological site from the 11th century"
          description="Fun fact headline shown while AI trip generation is in progress for Ubud (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ca8fs-JWCZSPu0eI1-En"
          defaultMessage="A menacing demon mouth marks the entrance to meditation chambers carved into rock. Dutch archaeologists only rediscovered the bathing pools in 1954 under a layer of jungle."
          description="Fun fact detail shown while AI trip generation is in progress for Ubud (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Emvc_FVDRIXbTHaTMnP3"
          defaultMessage="The Campuhan Ridge Walk was originally a royal horse trail"
          description="Fun fact headline shown while AI trip generation is in progress for Ubud (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="OnKZtuPZfjZmRot_KT4y"
          defaultMessage="This free, 2-km hilltop path offers panoramic valley views and cool breezes at sunrise. Art cafés at the village end serve coconut pancakes to reward early hikers."
          description="Fun fact detail shown while AI trip generation is in progress for Ubud (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="J2sIc1ve5-XUv78eVpRm"
          defaultMessage="The Bali Spirit Festival blends yoga, dance and live music"
          description="Fun fact headline shown while AI trip generation is in progress for Ubud (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="KrrK9NiBvNBovJnDgnUq"
          defaultMessage="Held each spring, it attracts thousands of practitioners from more than 50 countries. Proceeds support local environmental and educational charities around Ubud."
          description="Fun fact detail shown while AI trip generation is in progress for Ubud (fact 8)"
        />
      ),
    },
  ],
  Kuta: [
    {
      text: (
        <FormattedMessage
          id="wqpHgd_6Xs-HurIHKjVC"
          defaultMessage="Kuta was originally a quiet fishing village"
          description="Fun fact headline shown while AI trip generation is in progress for Kuta (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="iUlbuAiP-mjadTPTUvqX"
          defaultMessage="It didn’t transform into a global backpacker hotspot until the surfer boom of the 1970s. Traces of its maritime roots linger in early-morning fish auctions near the south end of the beach."
          description="Fun fact detail shown while AI trip generation is in progress for Kuta (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="NY3DU7S4LX7LM8AQhwkB"
          defaultMessage="Kuta Beach stretches about 5 km from Tuban up to Legian"
          description="Fun fact headline shown while AI trip generation is in progress for Kuta (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="dlGdhGBAoe2N26Y5LnWQ"
          defaultMessage="It’s one of Bali’s longest continuous shorelines, perfect for sunrise jogs or sunset soccer. Gentle, rolling waves make it a beginner-friendly surf break."
          description="Fun fact detail shown while AI trip generation is in progress for Kuta (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="2JbAJcmcLXoEouGya1rp"
          defaultMessage="Waterbom Bali in Kuta has been voted Asia’s #1 waterpark multiple times"
          description="Fun fact headline shown while AI trip generation is in progress for Kuta (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="1JtidWSH6_Uq-357MklD"
          defaultMessage="Spanning 3.8 ha, it packs in more than 17 adrenaline-pumping slides. Half of the park is landscaped with native plants to offset its carbon footprint."
          description="Fun fact detail shown while AI trip generation is in progress for Kuta (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="LXF-4g0gjFY8dJeNsOlV"
          defaultMessage="'Kuta' comes from the old Balinese word kutha, meaning fortress"
          description="Fun fact headline shown while AI trip generation is in progress for Kuta (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="gKmPj8r38IWLMY583z8q"
          defaultMessage="Historians believe a small stronghold once guarded this stretch of coast against pirates. Today only place-names like Banjar Kedonganan hint at the fortified past."
          description="Fun fact detail shown while AI trip generation is in progress for Kuta (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="BK9n5NsWKveeSKxkqbjp"
          defaultMessage="Every August Kuta hosts the Kuta Karnival"
          description="Fun fact headline shown while AI trip generation is in progress for Kuta (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="TAz68ox0aJ5GBfpAsebs"
          defaultMessage="Sand-sculpting contests, beach soccer tournaments and live music fill the foreshore for a week. Hotels collaborate on mass beach clean-ups before and after the party."
          description="Fun fact detail shown while AI trip generation is in progress for Kuta (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Z-AiGBLGa8Xzn_iTZ9By"
          defaultMessage="Kuta Art Market lights up nightly with 100+ stalls"
          description="Fun fact headline shown while AI trip generation is in progress for Kuta (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="GIjF-ybgcyhg3Imev_tF"
          defaultMessage="Vendors haggle over hand-woven ikats, carved masks and silver trinkets until late. It’s a lively spot to practise your Bahasa bargaining phrases."
          description="Fun fact detail shown while AI trip generation is in progress for Kuta (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Bz9h6YzhuQ_eWO31F_5V"
          defaultMessage="The area still celebrates full-moon ceremonies at local temples"
          description="Fun fact headline shown while AI trip generation is in progress for Kuta (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="3E1d0FylAiyy2s-mz-F3"
          defaultMessage="Expect gamelan orchestras, fragrant offerings and dancers in gold-threaded sarongs. Respectful visitors are welcome with a sash and modest dress."
          description="Fun fact detail shown while AI trip generation is in progress for Kuta (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="TBkrdeLTFIKH-GKHwsWn"
          defaultMessage="Ngurah Rai International Airport is just minutes from Kuta"
          description="Fun fact headline shown while AI trip generation is in progress for Kuta (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="dSY_d368w3kJmSDv2enF"
          defaultMessage="Its runways lie less than 3 km from the main strip, making Kuta one of Bali’s most convenient beach bases. Plane-spotters love photographing low-flying jets over the sand."
          description="Fun fact detail shown while AI trip generation is in progress for Kuta (fact 8)"
        />
      ),
    },
  ],
  Legian: [
    {
      text: (
        <FormattedMessage
          id="lDWKxqu4K4GEQY0Eloea"
          defaultMessage="Legian sits just north of Kuta yet feels a notch calmer"
          description="Fun fact headline shown while AI trip generation is in progress for Legian (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="nvxL41yd1V2IlTqMzXuV"
          defaultMessage="It shares those legendary Bali sunsets—just with smaller crowds and wider beach space. Surf schools dot the shoreline for first-timers."
          description="Fun fact detail shown while AI trip generation is in progress for Legian (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="qOl1bJj_rBPFW6rekpYg"
          defaultMessage="'Legian' in old Balinese literally means 'good sand'"
          description="Fun fact headline shown while AI trip generation is in progress for Legian (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ee5Koh1TT54EIUQvOSxF"
          defaultMessage="The shoreline stretches more than 2 km and is cleaned at dawn by swarms of community volunteers. Its fine grains are ideal for barefoot jogs."
          description="Fun fact detail shown while AI trip generation is in progress for Legian (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="bXv2GZOmxXp6TVnWXIIi"
          defaultMessage="La Plancha pioneered Bali’s beachfront lounge scene in 2006"
          description="Fun fact headline shown while AI trip generation is in progress for Legian (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="lFU2kyxJvBkwI64vk1Jj"
          defaultMessage="Its rainbow umbrellas and beanbags quickly became a social-media icon. Dozens of copy-cat bars now echo its colour-pop style along the coast."
          description="Fun fact detail shown while AI trip generation is in progress for Legian (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="QsJZG_dhB6RncJddp3yw"
          defaultMessage="Jalan Padma Utara is lined with boutiques selling local silversmith craft"
          description="Fun fact headline shown while AI trip generation is in progress for Legian (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ymhb7offW24bJdBBQmHd"
          defaultMessage="Balinese filigree earrings and chunky cuffs sparkle in window-lit displays. Artisans often work at benches in the back, happy to explain techniques."
          description="Fun fact detail shown while AI trip generation is in progress for Legian (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="RpHqknymxNr1Bf9AS3qy"
          defaultMessage="Legian celebrates purification ceremonies at Pura Dalem"
          description="Fun fact headline shown while AI trip generation is in progress for Legian (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Gj7_gGaveb1s-7a3zgYK"
          defaultMessage="On full-moon nights, locals carry barong lion masks and bamboo gamelan down the street. Visitors are welcome if shoulders and knees are covered."
          description="Fun fact detail shown while AI trip generation is in progress for Legian (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="cZEvCT8JUqM3sVa9ZV6z"
          defaultMessage="Padma Beach in Legian is a popular beginner surf spot"
          description="Fun fact headline shown while AI trip generation is in progress for Legian (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="YeK1ZB5JVB777JwpZHOw"
          defaultMessage="Gentle, rolling waves and soft sand bottom minimise wipe-out bruises. Board rentals and lessons cluster around the lifeguard tower."
          description="Fun fact detail shown while AI trip generation is in progress for Legian (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="p2bGFp2iNnSmUtXQ-M4x"
          defaultMessage="Legian rooftops offer panoramic views of South Bali"
          description="Fun fact headline shown while AI trip generation is in progress for Legian (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="nPNUKj1V0riKb1L9gAww"
          defaultMessage="Hotel sky-bars fill up at golden hour for cocktails above the palm skyline. Fireworks from nearby Kuta are visible on New Year’s Eve."
          description="Fun fact detail shown while AI trip generation is in progress for Legian (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="67ogjuvOHwu9xcgarUDx"
          defaultMessage="The Double Six Club helped launch Bali’s nightlife scene"
          description="Fun fact headline shown while AI trip generation is in progress for Legian (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ZqqQ3oD0NhrNWDueXhLX"
          defaultMessage="Although closed, its beachfront legacy lives on in modern venues like Jenja and Cocoon. Older expats still reminisce about its bungee jump over the dance floor."
          description="Fun fact detail shown while AI trip generation is in progress for Legian (fact 8)"
        />
      ),
    },
  ],
  Jimbaran: [
    {
      text: (
        <FormattedMessage
          id="R7EyHUr-w3zMLl8uMr_o"
          defaultMessage="Jimbaran Bay was once a humble fishing village famous for dawn fish markets"
          description="Fun fact headline shown while AI trip generation is in progress for Jimbaran (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="UvH3bypkuNowfsAzzMr6"
          defaultMessage="The Kedonganan Market still bustles at 5 a.m. as fishermen unload their catch. Chefs from five-star resorts barter alongside locals."
          description="Fun fact detail shown while AI trip generation is in progress for Jimbaran (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="91Ew_gCl3Tkq78I3k2F9"
          defaultMessage="Jimbaran Beach stretches for about 4 km of golden sand"
          description="Fun fact headline shown while AI trip generation is in progress for Jimbaran (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="M0tmRUCK2aD-G1WvUGJJ"
          defaultMessage="Its sheltered waters stay glassy even when the Bukit cliffs whip up surf elsewhere. Families favour the gentle shoreline for paddle-boarding."
          description="Fun fact detail shown while AI trip generation is in progress for Jimbaran (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="mIf08vNUJNF26LDoy9w3"
          defaultMessage="Jimbaran’s beachfront seafood cafés let you pick your own catch"
          description="Fun fact headline shown while AI trip generation is in progress for Jimbaran (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="-39uuy2QMTzqpt-PtRQA"
          defaultMessage="Choose fish, prawns or lobster by weight, then relax as chefs grill it in smoky coconut husks. Dining barefoot under tiki torches is a Bali rite of passage."
          description="Fun fact detail shown while AI trip generation is in progress for Jimbaran (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="4AlfjoXwTU-eeFBcR9HD"
          defaultMessage="Jimbaran’s famous sunset dinners under flame torches began in the 1970s"
          description="Fun fact headline shown while AI trip generation is in progress for Jimbaran (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="uJ2WVux9mOQlE5_gmhfW"
          defaultMessage="What started as fishermen hosting beach barbecues grew into a nightly 300-table phenomenon. Reservations are still scribbled on banana leaves."
          description="Fun fact detail shown while AI trip generation is in progress for Jimbaran (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="djo7RQMgH5WngLlWkWPd"
          defaultMessage="Uluwatu Temple sits on a cliff just west of Jimbaran Bay"
          description="Fun fact headline shown while AI trip generation is in progress for Jimbaran (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="fzFMCjy2Vbk_mg0NnFFj"
          defaultMessage="Its Kecak fire dance lights up the horizon after sunset. Many visitors stop in Jimbaran for seafood before the performance."
          description="Fun fact detail shown while AI trip generation is in progress for Jimbaran (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="99GL8SlxdYEVGmHV0l1w"
          defaultMessage="Traditional jukung boats still float offshore"
          description="Fun fact headline shown while AI trip generation is in progress for Jimbaran (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ilJsZXEgYzy1G8jCnksr"
          defaultMessage="Their bright batik colours help owners spot them among hundreds anchored at low tide. Photographers love the patterned reflections at sunrise."
          description="Fun fact detail shown while AI trip generation is in progress for Jimbaran (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="1xUlfIS2g7V6H_P34RDQ"
          defaultMessage="Jimbaran is popular for plane-spotting"
          description="Fun fact headline shown while AI trip generation is in progress for Jimbaran (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="G4OtdWrkaR-li3wHblIQ"
          defaultMessage="Jets skim low over the water on final approach to Ngurah Rai Airport. Beach cafés even list “arrival schedules” on chalkboards for aviation buffs."
          description="Fun fact detail shown while AI trip generation is in progress for Jimbaran (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="003yzuA0IWhuk48No-R3"
          defaultMessage="Limestone bluffs frame the bay like a natural amphitheatre"
          description="Fun fact headline shown while AI trip generation is in progress for Jimbaran (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="VU5l2u0Al2T7tRO8zry3"
          defaultMessage="They glow peach-pink during the famed Jimbaran sunset. Local kids cliff-jump from hidden ledges at high tide."
          description="Fun fact detail shown while AI trip generation is in progress for Jimbaran (fact 8)"
        />
      ),
    },
  ],
  Uluwatu: [
    {
      text: (
        <FormattedMessage
          id="-3TTmVhGKympPyKuw4fA"
          defaultMessage="'Pura Luhur Uluwatu' perches atop a 70 m limestone cliff"
          description="Fun fact headline shown while AI trip generation is in progress for Uluwatu (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Lilnt6RxMAQrkAU3fAiS"
          defaultMessage="Its name means 'Divine Temple of the Land’s End', one of Bali’s six key sea temples. Mischievous monkeys at the gate watch for unattended sunglasses."
          description="Fun fact detail shown while AI trip generation is in progress for Uluwatu (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="E6cLDEEzF5zr1-GRpZNB"
          defaultMessage="Uluwatu Temple is guarded by a troupe of macaques"
          description="Fun fact headline shown while AI trip generation is in progress for Uluwatu (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="B82tCngDyhhChVQ0EDp4"
          defaultMessage="They famously “trade” stolen items for fruit offered by local guides. Hold onto hats and phones while you admire the view."
          description="Fun fact detail shown while AI trip generation is in progress for Uluwatu (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="iUGuxd1IN0dxsbAZ7uOv"
          defaultMessage="You can catch the famous Kecak Fire Dance here at sunset"
          description="Fun fact headline shown while AI trip generation is in progress for Uluwatu (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ehVMC7_wntNlrhkZli_3"
          defaultMessage="Over 100 chanting men circle a blazing coconut-husk fire, re-enacting the Ramayana. The ocean horizon turns gold behind the open-air stage."
          description="Fun fact detail shown while AI trip generation is in progress for Uluwatu (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="0P0mdGoXV1W0ik6rJL_3"
          defaultMessage="Uluwatu is a world-class surf mecca, with waves up to 6 m high"
          description="Fun fact headline shown while AI trip generation is in progress for Uluwatu (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="JLmXP6oxgjfsVIWvOuVt"
          defaultMessage="The feared 'Uluwatu Right' break has drawn pros since the 1970s. Access is via a cave stairwell that opens straight into pounding sets."
          description="Fun fact detail shown while AI trip generation is in progress for Uluwatu (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="lD4_4dyAiT7a_C8CiwEW"
          defaultMessage="There’s a secret cave beneath the cliff"
          description="Fun fact headline shown while AI trip generation is in progress for Uluwatu (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="LTgQ9gUUTWs4ekXqDGDc"
          defaultMessage="Stone steps descend to a sheltered grotto once used for silent meditation. At low tide it reveals sea-carved archways perfect for photos."
          description="Fun fact detail shown while AI trip generation is in progress for Uluwatu (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="mLwFUrYu5uVxyjv5S6Cy"
          defaultMessage="Cliffside shrines line the Uluwatu walking path"
          description="Fun fact headline shown while AI trip generation is in progress for Uluwatu (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="XXxUGUSECRSq2P_omsH7"
          defaultMessage="Pilgrims leave marigold offerings and incense at each little pavilion. Sunrise walkers often spot sea eagles gliding on updrafts."
          description="Fun fact detail shown while AI trip generation is in progress for Uluwatu (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="AyYjXLG80iFLnE56v17g"
          defaultMessage="Padang Padang Beach lies just below the temple"
          description="Fun fact headline shown while AI trip generation is in progress for Uluwatu (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="OCfnRvuVmtcMMM27Bb-Y"
          defaultMessage="A narrow rock tunnel leads to powder-white sand and turquoise water. It hosted the 2010 Rip Curl Cup and Julia Roberts in “Eat Pray Love”."
          description="Fun fact detail shown while AI trip generation is in progress for Uluwatu (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="BiO2zy5XjFqbVsJ0WiVb"
          defaultMessage="Blowholes beneath the cliffs send water jets skyward"
          description="Fun fact headline shown while AI trip generation is in progress for Uluwatu (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="qHqI56PrjslAzbN2l1w8"
          defaultMessage="The spectacle peaks at high tide when swells slam cavern mouths below. Keep a safe distance—rogue spray can soak cameras from 50 m away."
          description="Fun fact detail shown while AI trip generation is in progress for Uluwatu (fact 8)"
        />
      ),
    },
  ],
  Singapore: [
    {
      text: (
        <FormattedMessage
          id="AkfDOM_RKeXwfUhhq9QR"
          defaultMessage="Singapore is made up of 63 islands"
          description="Fun fact headline shown while AI trip generation is in progress for Singapore (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="vBfJ0M5tMut6s382qnWP"
          defaultMessage="Beyond the main island, satellite isles like Sentosa, Pulau Ubin and St John’s each offer their own attractions. Several can be reached by cable-car, bumboat or even bicycle ferry."
          description="Fun fact detail shown while AI trip generation is in progress for Singapore (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="-HDtuvEK8JFiZMO2cVxx"
          defaultMessage="The world’s tallest indoor waterfall lives at Changi Airport"
          description="Fun fact headline shown while AI trip generation is in progress for Singapore (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="2uk-OLQOEWr7GwFPyF8G"
          defaultMessage="Jewel’s Rain Vortex plunges 40 m through a terraced rainforest atrium, sending mist across five storeys of shops. Nightly light-and-sound shows turn the cascade into a technicolour vortex."
          description="Fun fact detail shown while AI trip generation is in progress for Singapore (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="iiaDh9hCB8JJ6kqDA5cn"
          defaultMessage="Almost half the city is green space"
          description="Fun fact headline shown while AI trip generation is in progress for Singapore (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="teqTS1HsAY3SKouv9z4c"
          defaultMessage="Tree-lined streets, pocket parks and rooftop gardens earn Singapore the nickname “City in a Garden.” Flagship projects like Gardens by the Bay showcase futuristic “Supertrees” that harvest solar power."
          description="Fun fact detail shown while AI trip generation is in progress for Singapore (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="EyjadeNdpT7uo-Z9QqEc"
          defaultMessage="Chewing gum is famously restricted"
          description="Fun fact headline shown while AI trip generation is in progress for Singapore (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="T9AY9khVia81-AoE1m5R"
          defaultMessage="Only therapeutic gum is legal, keeping pavements spotless and trains litter-free. Fines for illegal import or spitting can reach S$2,000."
          description="Fun fact detail shown while AI trip generation is in progress for Singapore (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="4FS-HJWFFKF51qRwKlgH"
          defaultMessage="The MRT is fully driverless"
          description="Fun fact headline shown while AI trip generation is in progress for Singapore (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="TpJtRDuDHRzHN60h1rMb"
          defaultMessage="Automated trains on the Red and Green lines whisk about three million passengers around daily. Platform screen doors and A/C stations make commutes comfortable in tropical heat."
          description="Fun fact detail shown while AI trip generation is in progress for Singapore (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="UXlvj-k3qBTjz90IKY2d"
          defaultMessage="F1’s first night race roars through Marina Bay"
          description="Fun fact headline shown while AI trip generation is in progress for Singapore (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="GLK1s0AcKSQyPCsbuJdW"
          defaultMessage="Since 2008, the Singapore Grand Prix has lit up a 23-corner street circuit with 1,600 floodlights. Drivers call the humid, stop-start course one of the toughest on the calendar."
          description="Fun fact detail shown while AI trip generation is in progress for Singapore (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="OSdVsxPFCvV1EXK6RhLm"
          defaultMessage="The Merlion statue sprays 2,400 L of water a minute"
          description="Fun fact headline shown while AI trip generation is in progress for Singapore (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="CdzO5b-aD6-6tESpJ7wm"
          defaultMessage="Half-lion, half-fish, the 8.6-m mascot symbolises Singapore’s maritime trading roots. Tourists queue day and night for the classic skyline selfie beside its fountain."
          description="Fun fact detail shown while AI trip generation is in progress for Singapore (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="2nLoBNNRUyXHuntdx99f"
          defaultMessage="Hawker culture is UNESCO-listed"
          description="Fun fact headline shown while AI trip generation is in progress for Singapore (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="GQvfbiGcSWND0Vl01EAw"
          defaultMessage="Open-air food centres preserve dishes like chicken rice, laksa and satay at affordable prices. Tables are shared “chope-style” with tissue packets, fostering a uniquely communal dining scene."
          description="Fun fact detail shown while AI trip generation is in progress for Singapore (fact 8)"
        />
      ),
    },
  ],
  London: [
    {
      text: (
        <FormattedMessage
          id="08FcJkydXowx7-2l03dl"
          defaultMessage="London’s buses weren’t always red"
          description="Fun fact headline shown while AI trip generation is in progress for London (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="mhcGR2mukgKZNs1hdIOW"
          defaultMessage="In the 1800s each private operator painted its double-deckers a different colour so passengers could spot their route at a glance. The network was unified under the iconic red livery in 1907, creating a moving city-wide brand."
          description="Fun fact detail shown while AI trip generation is in progress for London (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="4hQfxKeLxQlNyv0R9UV8"
          defaultMessage="The London Underground is the world’s oldest metro system"
          description="Fun fact headline shown while AI trip generation is in progress for London (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="yOH9f0J2jn_FcWChzD0B"
          defaultMessage="It opened in 1863 with steam trains puffing through brick-lined “cut-and-cover” tunnels. Today its 11 lines carry about five million passenger journeys on busy weekdays."
          description="Fun fact detail shown while AI trip generation is in progress for London (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="xyNcIfzYa6QcXjcOzdy3"
          defaultMessage="Big Ben doesn’t refer to the tower"
          description="Fun fact headline shown while AI trip generation is in progress for London (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="RU4JatqWOSaFEN43qHZD"
          defaultMessage="It is the nickname of the Great Bell that chimes inside the clock at the north end of the Palace of Westminster. The tower itself was renamed Elizabeth Tower in 2012 for Queen Elizabeth II’s Diamond Jubilee."
          description="Fun fact detail shown while AI trip generation is in progress for London (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="BymlhDY7ow2MVud6PEDO"
          defaultMessage="There are over 170 museums in London"
          description="Fun fact headline shown while AI trip generation is in progress for London (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="lDHexkUhJSuXtfeH0h02"
          defaultMessage="Flagships like the British Museum, Natural History Museum and Tate Modern all offer free entry thanks to public funding. Together they welcome more than 65 million visits a year, making culture remarkably accessible."
          description="Fun fact detail shown while AI trip generation is in progress for London (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="k4OFKKauiDKlKhmkvuj4"
          defaultMessage="The River Thames has over 30 bridges in Greater London"
          description="Fun fact headline shown while AI trip generation is in progress for London (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="4yHp5xV2-40Fc5tKL7uK"
          defaultMessage="The oldest is London Bridge, though the current structure dates from 1973 with granite from its Victorian predecessor. Free pedestrian crossings make riverside walks one of the easiest ways to sight-see."
          description="Fun fact detail shown while AI trip generation is in progress for London (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="8EwYTwjYIJNnxiXo8VgR"
          defaultMessage="Tower Bridge once raised its bascules over 6,000 times a year"
          description="Fun fact headline shown while AI trip generation is in progress for London (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="tpT-56nSuR8IZMpyehHx"
          defaultMessage="Today it lifts only around 800 times annually—still an impressive feat of Victorian engineering. The hydraulics are original in design but now run on oil and electricity instead of steam."
          description="Fun fact detail shown while AI trip generation is in progress for London (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="P6XYkZd8CBjXw12oD1jI"
          defaultMessage="London has more than 8 million trees"
          description="Fun fact headline shown while AI trip generation is in progress for London (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="gVW6iuMHCGuLfn6G6ri0"
          defaultMessage="That is roughly one tree per resident and provides 21 % canopy cover across the capital. In 2019 the city declared itself a “National Park City” to celebrate its unexpectedly green landscape."
          description="Fun fact detail shown while AI trip generation is in progress for London (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="QshtekXsKJiXHg0IUAg6"
          defaultMessage="The Shard is the tallest building in the UK"
          description="Fun fact headline shown while AI trip generation is in progress for London (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="q-2SzDn6TaOk9F-pcehw"
          defaultMessage="Soaring 310 m, its glass “shards” were modelled on church spires painted by Canaletto. The open-air Skydeck on level 72 offers 360-degree views stretching up to 65 km on a clear day."
          description="Fun fact detail shown while AI trip generation is in progress for London (fact 8)"
        />
      ),
    },
  ],
  Dubai: [
    {
      text: (
        <FormattedMessage
          id="3ohMVjx_NwLLshF2A6Ht"
          defaultMessage="Dubai is home to the world’s tallest building"
          description="Fun fact headline shown while AI trip generation is in progress for Dubai (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="BJ6jtjUAKMdEe9_AV7Tz"
          defaultMessage="The Burj Khalifa pierces the sky at 828 m and 163 floors, dwarfing surrounding skyscrapers. Visitors ride the world’s fastest double-deck elevators to an open-air deck on level 148."
          description="Fun fact detail shown while AI trip generation is in progress for Dubai (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="GMSds1EZx6574CvpmcYt"
          defaultMessage="DXB is the world’s busiest airport for international travellers"
          description="Fun fact headline shown while AI trip generation is in progress for Dubai (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="9UKtg_RrcCBF2Bg-UHI4"
          defaultMessage="More than 86 million passengers passed through in 2024, fuelled by Emirates’ globe-spanning hub-and-spoke network. Three terminals process up to 100 aircraft movements an hour."
          description="Fun fact detail shown while AI trip generation is in progress for Dubai (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="u-zakwJSI4BKf_5qstic"
          defaultMessage="The Dubai Metro is completely driverless"
          description="Fun fact headline shown while AI trip generation is in progress for Dubai (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="MLupCpfzutbfDqml0c0s"
          defaultMessage="Red and Green lines span 89 km of elevated and underground track, all controlled by a central computer. Every station is air-conditioned, and gold-class carriages offer front-window views."
          description="Fun fact detail shown while AI trip generation is in progress for Dubai (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="vnWy71EmWETfwGKF1bMm"
          defaultMessage="Ski Dubai brings snow to the desert"
          description="Fun fact headline shown while AI trip generation is in progress for Dubai (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="9Lcc0cHwf5I9cQaCWzwM"
          defaultMessage="The 22,500 m² indoor slope maintains –4 °C year-round, with five runs and a chairlift beneath the Mall of the Emirates. Real king penguins waddle out for meet-and-greets several times a day."
          description="Fun fact detail shown while AI trip generation is in progress for Dubai (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="6g8KAzt0mEpUoNK0-oG3"
          defaultMessage="Palm Jumeirah added 560 ha of land to the coastline"
          description="Fun fact headline shown while AI trip generation is in progress for Dubai (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="h0S3ohKPuV8-CK6lx7-Q"
          defaultMessage="The palm-shaped archipelago required 94 million m³ of sand dredged from the Gulf. A monorail now links luxury villas and the Atlantis resort to the mainland."
          description="Fun fact detail shown while AI trip generation is in progress for Dubai (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="tuHOnchNXPnYdUQD3OcA"
          defaultMessage="Gold vending machines exist here"
          description="Fun fact headline shown while AI trip generation is in progress for Dubai (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="27UmVYfu55JT6mf4Y-Rs"
          defaultMessage="ATMs branded “Gold to Go” dispense everything from 1-g wafers to 10-g bars, updated to real-time bullion prices. Customers can even choose engraved designs as souvenirs."
          description="Fun fact detail shown while AI trip generation is in progress for Dubai (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="6pUZMnlhEnGqHu4HPxhk"
          defaultMessage="Dubai Police patrol in supercars"
          description="Fun fact headline shown while AI trip generation is in progress for Dubai (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="gqoscYOPqcs9s7-uKNy8"
          defaultMessage="Their fleet includes a Bugatti Veyron, Lamborghini Aventador and Aston Martin One-77, all painted green-and-white. The eye-catching vehicles double as tourism ambassadors at public events."
          description="Fun fact detail shown while AI trip generation is in progress for Dubai (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="bCti4AXb1Rdnr5uVkUd0"
          defaultMessage="Over 85 % of residents are expatriates"
          description="Fun fact headline shown while AI trip generation is in progress for Dubai (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Iw1CodtJOnXdXB8lc_wr"
          defaultMessage="More than 200 nationalities live side-by-side, creating a culinary scene that ranges from Afghan kebabs to Zimbabwean sadza. English functions as a common lingua franca alongside Arabic."
          description="Fun fact detail shown while AI trip generation is in progress for Dubai (fact 8)"
        />
      ),
    },
  ],
  'New York': [
    {
      text: (
        <FormattedMessage
          id="t5bdQ4d38KG5cZtUuKFH"
          defaultMessage="Over 800 languages are spoken in New York City"
          description="Fun fact headline shown while AI trip generation is in progress for New York (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Mif4Mdb7yuJd2Y60FIR6"
          defaultMessage="Queens alone is considered the most linguistically diverse urban area on Earth. Street signs in neighbourhoods like Jackson Heights read in Spanish, Bengali and Tibetan side-by-side."
          description="Fun fact detail shown while AI trip generation is in progress for New York (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="9yCZt5J7gSzsUYvGLp7Y"
          defaultMessage="Central Park is bigger than Monaco"
          description="Fun fact headline shown while AI trip generation is in progress for New York (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="_KJp74f1kQIMZ0vglHiB"
          defaultMessage="The 3.41-km² greensward welcomes roughly 42 million visitors each year. Horse-drawn carriages, paddle-boats and winter ice-skating change with the seasons."
          description="Fun fact detail shown while AI trip generation is in progress for New York (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="hcJ8N0x7oYUNuoAvpbig"
          defaultMessage="The Empire State Building was built in just 410 days"
          description="Fun fact headline shown while AI trip generation is in progress for New York (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="nJVwsPAysXtmTLqvyYXu"
          defaultMessage="At peak, construction leapt four and a half stories per week—an unrivalled pace in 1931. The mooring mast on top was originally intended for dirigibles."
          description="Fun fact detail shown while AI trip generation is in progress for New York (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="QJAQhM1zeWbce0L5h1wx"
          defaultMessage="NYC’s subway spans 472 stations"
          description="Fun fact headline shown while AI trip generation is in progress for New York (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="C19o4EWqqROwWLbqKttz"
          defaultMessage="If its 392 track-km were laid end-to-end they would stretch well past Chicago. Express tracks let trains overtake locals in the same tunnel."
          description="Fun fact detail shown while AI trip generation is in progress for New York (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="qRElJPzd_sux7UIRvqGF"
          defaultMessage="Times Square was named after a newspaper"
          description="Fun fact headline shown while AI trip generation is in progress for New York (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Jbw9-r7IVpExHQtG9vW8"
          defaultMessage="The *New York Times* moved its HQ there in 1904, though it stayed only nine years. The name and neon glow endured long after the presses moved on."
          description="Fun fact detail shown while AI trip generation is in progress for New York (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="JyKPp7250rD53Gcmcvb1"
          defaultMessage="The 'pizza-slice index' tracks subway fares"
          description="Fun fact headline shown while AI trip generation is in progress for New York (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="s2jH_RZ7ZeTLdsYj-wSb"
          defaultMessage="Since the 1960s the price of a cheese slice has hovered close to the cost of a single ride. Economists still quote the ratio as a tongue-in-cheek inflation gauge."
          description="Fun fact detail shown while AI trip generation is in progress for New York (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="eE20EL393fKQ5Y4gVsKp"
          defaultMessage="The Statue of Liberty arrived in 350 pieces"
          description="Fun fact headline shown while AI trip generation is in progress for New York (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="a55mCQLz_9ukHCL36TpJ"
          defaultMessage="French workers packed the copper plates into 214 crates for the 1885 voyage. Reassembly on Bedloe’s Island took four months of riveting."
          description="Fun fact detail shown while AI trip generation is in progress for New York (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="M-exIkovJ17QATxCzfVx"
          defaultMessage="Grand Central has a secret Platform 61"
          description="Fun fact headline shown while AI trip generation is in progress for New York (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="JfLCRINDfmTCai8JItpE"
          defaultMessage="It once whisked President Franklin D. Roosevelt’s armour-plated limousine directly into the Waldorf Astoria garage. The track now lies dormant but intact beneath Park Avenue."
          description="Fun fact detail shown while AI trip generation is in progress for New York (fact 8)"
        />
      ),
    },
  ],

  Honolulu: [
    {
      text: (
        <FormattedMessage
          id="ro63WQmbGSLn-qJR7oZ4"
          defaultMessage="ʻIolani Palace is America’s only royal residence"
          description="Fun fact headline shown while AI trip generation is in progress for Honolulu (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="oZh3pu7U2mqCo5blDvuC"
          defaultMessage="The Hawaiian monarchs ruled from its gilded halls until the overthrow of 1893. Remarkably, the palace had electric lights four years before the White House."
          description="Fun fact detail shown while AI trip generation is in progress for Honolulu (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="93uef3kg_5AOH_oMpzy_"
          defaultMessage="Waikiki popularised modern surfing worldwide"
          description="Fun fact headline shown while AI trip generation is in progress for Honolulu (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="azzBGt-oG9C6nXSw3pwF"
          defaultMessage="Olympic swimmer Duke Kahanamoku taught tourists to ride longboards here in the early 1900s. Today gentle rollers still make it the ultimate beginner break."
          description="Fun fact detail shown while AI trip generation is in progress for Honolulu (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="fmOnd9-a3qRJvDsvDaO-"
          defaultMessage="Diamond Head is an extinct volcanic crater"
          description="Fun fact headline shown while AI trip generation is in progress for Honolulu (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="kHKz-TZToV39W7CYiuJ4"
          defaultMessage="A 99-step trail climbs to bunkers used as WWII lookout posts. The summit rewards hikers with panoramic views over Waikiki’s hotel strip."
          description="Fun fact detail shown while AI trip generation is in progress for Honolulu (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="X7Qc81Cdbv_3IH5oEZa9"
          defaultMessage="The Honolulu Marathon is among the world’s largest"
          description="Fun fact headline shown while AI trip generation is in progress for Honolulu (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="bgJ-qskg1_R-S7ivom4M"
          defaultMessage="Over 30,000 runners tackle the humid December course each year. There is no time limit, so thousands of first-timers walk the route to fireworks at the finish."
          description="Fun fact detail shown while AI trip generation is in progress for Honolulu (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="RDTGW0TL7yLqu4SooG26"
          defaultMessage="Hawaii consumes more Spam per capita than any U.S. state"
          description="Fun fact headline shown while AI trip generation is in progress for Honolulu (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="wWlHpHWG-UQqPmk0Z2VQ"
          defaultMessage="Locals slice the canned meat into sushi-style Spam musubi or fry it for breakfast. Even McDonald’s offers Spam and eggs on its island menus."
          description="Fun fact detail shown while AI trip generation is in progress for Honolulu (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="x5MlTOhTukp5a9bSa9y5"
          defaultMessage="Ala Moana Center is the world’s biggest open-air mall"
          description="Fun fact headline shown while AI trip generation is in progress for Honolulu (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="amXZLzDjByKKQHt8-_nd"
          defaultMessage="More than 350 shops sprawl across four breezy levels linked by koi ponds and bridges. Each Christmas a 15-m tree lights up its central stage."
          description="Fun fact detail shown while AI trip generation is in progress for Honolulu (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="0L961a0W3HwJMlWRhElP"
          defaultMessage="The USS Arizona Memorial floats above a sunken battleship"
          description="Fun fact headline shown while AI trip generation is in progress for Honolulu (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="S5_rvjT6NYd1JB4B8bTQ"
          defaultMessage="Oil still beads to the surface—dubbed the “black tears of the Arizona.” The shrine honours 1,177 sailors lost in the 1941 Pearl Harbor attack."
          description="Fun fact detail shown while AI trip generation is in progress for Honolulu (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="9KRbT63csgUydsQFJTQP"
          defaultMessage="Honolulu’s average temperature varies only 4 °C year-round"
          description="Fun fact headline shown while AI trip generation is in progress for Honolulu (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="aofCzGdwkP8PCztkKY9j"
          defaultMessage="Steady trade winds keep humidity in check and make the climate feel pleasantly mild. Locals joke they own more board shorts than jackets."
          description="Fun fact detail shown while AI trip generation is in progress for Honolulu (fact 8)"
        />
      ),
    },
  ],
  Hawaii: [
    {
      text: (
        <FormattedMessage
          id="d2xShoqLviHS6ZOWnOPt"
          defaultMessage="Hawaii sits atop a volcanic hotspot"
          description="Fun fact headline shown while AI trip generation is in progress for Hawaii (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="HrUoxoLLTkMaGyegNZRp"
          defaultMessage="Kīlauea in Hawaiʻi Volcanoes National Park has erupted almost continuously since 1983. Glow from its lava lake is often visible after dark from park lookouts."
          description="Fun fact detail shown while AI trip generation is in progress for Hawaii (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="ou_qTbw4rwFmm_PPHj5N"
          defaultMessage="It’s the only U.S. state with two official languages"
          description="Fun fact headline shown while AI trip generation is in progress for Hawaii (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ziejHFq1Nq24vjUcOMXM"
          defaultMessage="Hawaiian and English share equal legal status, while Pidgin creole is widely spoken in everyday conversation. Road signs increasingly carry bilingual names."
          description="Fun fact detail shown while AI trip generation is in progress for Hawaii (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="ScZJYRemqORycOap_CWc"
          defaultMessage="New land grows by about 42 acres a year"
          description="Fun fact headline shown while AI trip generation is in progress for Hawaii (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="wLjRC6E8unG-uLv-xkAv"
          defaultMessage="Cooling lava flowing into the Pacific gradually extends the island’s shoreline. A 2018 eruption even created a new black-sand beach at Kapoho Bay."
          description="Fun fact detail shown while AI trip generation is in progress for Hawaii (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="YaysQpygLYI70AtQi__a"
          defaultMessage="Mauna Kea is the tallest mountain on Earth—if measured from base to peak"
          description="Fun fact headline shown while AI trip generation is in progress for Hawaii (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="yPdgtUtEmHT_tYkw4Hgs"
          defaultMessage="From the ocean floor it towers around 10,210 m, beating Everest by nearly a kilometre. Its 4,207-m summit hosts world-class telescopes above 40 % of Earth’s atmosphere."
          description="Fun fact detail shown while AI trip generation is in progress for Hawaii (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Xnm-5f3P6eUggp5mBSkv"
          defaultMessage="Hawaii has no daylight saving time"
          description="Fun fact headline shown while AI trip generation is in progress for Hawaii (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Xu8rMYEO2lob4D2bZjw4"
          defaultMessage="Clocks stay the same all year, so sunset times drift only modestly with the seasons. Jet-lagged visitors appreciate the consistent early sunsets for stargazing."
          description="Fun fact detail shown while AI trip generation is in progress for Hawaii (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="2VC7OA0f4WSZ1Xfexaqz"
          defaultMessage="The Hawaiian alphabet has just 12 letters"
          description="Fun fact headline shown while AI trip generation is in progress for Hawaii (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="5LcAdU6XibRZFSAe_ob3"
          defaultMessage="Five vowels and seven consonants spell every word, plus the ʻokina glottal stop. This simplicity gives place names their rhythmic character—think **Haleakalā** and **Lāhainā**."
          description="Fun fact detail shown while AI trip generation is in progress for Hawaii (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="BqTuwRmTjaqeM23N3PNC"
          defaultMessage="Humpback whales migrate here each winter"
          description="Fun fact headline shown while AI trip generation is in progress for Hawaii (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Q1XaiD_oG3HM3isGOH1a"
          defaultMessage="Roughly 10,000 gather to breed between December and April, especially off Maui’s west coast. Breaching displays are visible even from beachfront cafés."
          description="Fun fact detail shown while AI trip generation is in progress for Hawaii (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="gEXPwiV5fI8FZ41ab4V3"
          defaultMessage="Rainbow licence plates honour Hawaii’s frequent arcs"
          description="Fun fact headline shown while AI trip generation is in progress for Hawaii (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Ct6yVm6c1glVs3mjNaSW"
          defaultMessage="Moist trade winds and sharp mountain ridges create prime rainbow-making conditions. Meteorologists say the islands see more rainbows per day than anywhere else in the U.S."
          description="Fun fact detail shown while AI trip generation is in progress for Hawaii (fact 8)"
        />
      ),
    },
  ],
  Nadi: [
    {
      text: (
        <FormattedMessage
          id="g8Xvi159wdPaTqKlRLFG"
          defaultMessage="Nadi International Airport handles 97 % of Fiji’s visitors"
          description="Fun fact headline shown while AI trip generation is in progress for Nadi (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ZoQG95EGM2mWo7Faraz8"
          defaultMessage="Its WWII runways evolved into the nation’s main gateway and can now accommodate Airbus A380s. A modernisation in 2020 added open-air atriums filled with frangipani."
          description="Fun fact detail shown while AI trip generation is in progress for Nadi (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="9QW-dBJA2J0BV1eoo2eI"
          defaultMessage="Sri Siva Subramaniya is the largest Hindu temple in the Southern Hemisphere"
          description="Fun fact headline shown while AI trip generation is in progress for Nadi (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="q0xsDQjBCWlpbKzwnyIq"
          defaultMessage="Builders used traditional Dravidian stone-carving techniques over seven years. Intricate gopuram towers burst with rainbow colours against the tropical sky."
          description="Fun fact detail shown while AI trip generation is in progress for Nadi (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="e5uu8sxBBEFxtr7b-6Kx"
          defaultMessage="'Nadi' means 'river' in Fijian"
          description="Fun fact headline shown while AI trip generation is in progress for Nadi (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="cJVRCWB0UCuXuaDEZQJL"
          defaultMessage="The town grew on the fertile banks of the meandering Nadi River. Sugar-cane plantations still dominate the surrounding floodplain."
          description="Fun fact detail shown while AI trip generation is in progress for Nadi (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="JAo6OkGvTrUEaVZiSa6X"
          defaultMessage="The Bula Festival has run since 1960"
          description="Fun fact headline shown while AI trip generation is in progress for Nadi (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="4QidPy04i2zXR_BDc_yH"
          defaultMessage="Week-long parades, live music and a Lady Bula pageant raise funds for local charities. Street stalls sell kokoda (citrus-cured fish) and pineapple ice blocks."
          description="Fun fact detail shown while AI trip generation is in progress for Nadi (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="oqvCBINmxKmEQdTGsEQR"
          defaultMessage="Denarau Island was reclaimed from mangrove swamp"
          description="Fun fact headline shown while AI trip generation is in progress for Nadi (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="CM6gBouDX4nF8ht09Hay"
          defaultMessage="A short causeway now links luxury resorts and an 18-hole championship golf course to the mainland. It’s the departure point for most Yasawa and Mamanuca cruises."
          description="Fun fact detail shown while AI trip generation is in progress for Nadi (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="SDw3a141CBFbA9BuNCJ_"
          defaultMessage="The Garden of the Sleeping Giant holds 2,000 orchid varieties"
          description="Fun fact headline shown while AI trip generation is in progress for Nadi (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="QNXCKARptaKxCXc1v9KJ"
          defaultMessage="Actor Raymond Burr started the collection in 1977, filling the foothills with dendrobiums and cattleyas. Raised boardwalks wind beneath giant Tahitian trees and lily ponds."
          description="Fun fact detail shown while AI trip generation is in progress for Nadi (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Yp3EOsuBIkgY-HKtQ2Uu"
          defaultMessage="Nadi is launch-point for the Mamanuca Islands"
          description="Fun fact headline shown while AI trip generation is in progress for Nadi (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="t-0t6wj4CXhDxXK-Nr0y"
          defaultMessage="Over 20 postcard sand cays sit less than an hour away by fast catamaran. Day cruises offer snorkelling, coconut husking demos and kava ceremonies."
          description="Fun fact detail shown while AI trip generation is in progress for Nadi (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="7Ulz1Eds1fBFjj8ctHK6"
          defaultMessage="Traditional bure huts still dot nearby villages"
          description="Fun fact headline shown while AI trip generation is in progress for Nadi (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="TR35bMGicvYlzDCEa0TR"
          defaultMessage="Palm-leaf walls and thatched roofs follow centuries-old construction methods. Roofs are replaced every five or six years during communal “somo” work parties."
          description="Fun fact detail shown while AI trip generation is in progress for Nadi (fact 8)"
        />
      ),
    },
  ],
  'Coral Coast': [
    {
      text: (
        <FormattedMessage
          id="1_RfowRwHM0BMHr5z2hV"
          defaultMessage="The Coral Coast stretches 50 km along Viti Levu’s south"
          description="Fun fact headline shown while AI trip generation is in progress for Coral Coast (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="rsedKllPnDFm_ZeTWD3h"
          defaultMessage="A fringing reef hugs the shoreline almost the entire way, creating calm lagoons. Snorkellers can spot clownfish just metres off the sand."
          description="Fun fact detail shown while AI trip generation is in progress for Coral Coast (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="IqfdhalW3_dbdRVJn7FW"
          defaultMessage="Sigatoka Sand Dunes were Fiji’s first national park"
          description="Fun fact headline shown while AI trip generation is in progress for Coral Coast (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Jp6pTCyUc3nfH809Vokn"
          defaultMessage="Archaeologists uncovered 2,600-year-old Lapita pottery and ancient burial sites here. Interpretive walks reveal shifting dune ecosystems unlike anywhere else in Fiji."
          description="Fun fact detail shown while AI trip generation is in progress for Coral Coast (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="tmhqeDwA4NL38FGsAVz-"
          defaultMessage="Natadola Beach is often rated Fiji’s best"
          description="Fun fact headline shown while AI trip generation is in progress for Coral Coast (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Z91hNCQyf8XdiY5LXyGe"
          defaultMessage="Its deep lagoon allows safe swimming at any tide and horses trot the shoreline for sunset rides. The adjoining championship golf course overlooks turquoise reef."
          description="Fun fact detail shown while AI trip generation is in progress for Coral Coast (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="SnjWr37395m7qCvzSxsF"
          defaultMessage="The Coral Coast Railway runs sugar-cane trains turned tourist rides"
          description="Fun fact headline shown while AI trip generation is in progress for Coral Coast (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="yDwPfhRIKXPy2eyZYNA-"
          defaultMessage="Open-air carriages chug from plantation fields to coastal villages, stopping for kava tastings. During harvest season, the same narrow-gauge line hauls cane to the Lautoka mill."
          description="Fun fact detail shown while AI trip generation is in progress for Coral Coast (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Rpyf26ZtCocOXaW4lhxf"
          defaultMessage="Kula Eco Park breeds endangered Fijian crested iguanas"
          description="Fun fact headline shown while AI trip generation is in progress for Coral Coast (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="E5Nvo6btZ5moQMnoGPJP"
          defaultMessage="Visitors can hand-feed native parrots and hold juvenile boa constrictors. Profits fund mangrove replanting and school conservation workshops."
          description="Fun fact detail shown while AI trip generation is in progress for Coral Coast (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="LaqkvwC6Tc0CdfR2qm7p"
          defaultMessage="Tavuni Hill Fort was built by Tongan chiefs in the 1700s"
          description="Fun fact headline shown while AI trip generation is in progress for Coral Coast (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="QEgsnaFROLL0ksqXqk26"
          defaultMessage="Clifftop earthworks defended against rival cannibal tribes. Interpretive signs retell grisly legends while offering panoramic river-valley views."
          description="Fun fact detail shown while AI trip generation is in progress for Coral Coast (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="C6t74n4dMMGOft2_E3q4"
          defaultMessage="Beqa Lagoon nearby is famous for shark dives"
          description="Fun fact headline shown while AI trip generation is in progress for Coral Coast (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="X605udLAJT7X843_UPG1"
          defaultMessage="Bull, tiger and reef sharks gather for controlled hand-feeding at depths of 20 m. Divers wear chain-mail gloves while guides carry aluminium poles for safety."
          description="Fun fact detail shown while AI trip generation is in progress for Coral Coast (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Z62VTIOFgJ17ZMOUGgIq"
          defaultMessage="Villagers still perform traditional fire-walking ceremonies"
          description="Fun fact headline shown while AI trip generation is in progress for Coral Coast (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="dqblEjWvpS2ObO5TaS-f"
          defaultMessage="According to legend the skill was a gift from the spirit god Tui Namoliwai. Participants stroll barefoot across 500 °C stones without burns."
          description="Fun fact detail shown while AI trip generation is in progress for Coral Coast (fact 8)"
        />
      ),
    },
  ],
  'Xuyen Moc': [
    {
      text: (
        <FormattedMessage
          id="x6YhQUr1Nn4m6EOHYfn4"
          defaultMessage="Xuyên Mộc boasts 32 km of pristine coastline"
          description="Fun fact headline shown while AI trip generation is in progress for Xuyen Moc (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="u8w1BIphzIaTRP6au4C7"
          defaultMessage="Untouristed stretches like Hồ Cốc and Hồ Tràm curve along the East Sea under casuarina pines. Quiet weekdays make them perfect for solitary sunrise picnics."
          description="Fun fact detail shown while AI trip generation is in progress for Xuyen Moc (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="-5WyOsCCfSJ6uVLUyrnd"
          defaultMessage="Bình Châu–Phước Bửu Nature Reserve protects 11,300 ha of forest"
          description="Fun fact headline shown while AI trip generation is in progress for Xuyen Moc (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="XVfkz5LrCACGstI3rFxP"
          defaultMessage="Wild elephants, macaques and sun bears roam its dipterocarp habitat. Birders come seeking the rare Siamese fireback pheasant on misty dawn trails."
          description="Fun fact detail shown while AI trip generation is in progress for Xuyen Moc (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="u9xHait4kyA2h6fOJyzI"
          defaultMessage="Bình Châu Hot Springs bubble at up to 82 °C"
          description="Fun fact headline shown while AI trip generation is in progress for Xuyen Moc (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="QWuppbJwmlQBMk2_EJPD"
          defaultMessage="Locals boil eggs in wicker baskets for onsen-style snacks beside mineral pools. Resorts pipe the sulphur-rich water into cedar tubs for therapeutic soaks."
          description="Fun fact detail shown while AI trip generation is in progress for Xuyen Moc (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="OTdsVqaKvshxA3h8Zarc"
          defaultMessage="Pepper plantations blanket inland hills"
          description="Fun fact headline shown while AI trip generation is in progress for Xuyen Moc (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="OVaY1Ly3SswUKIt-1f_S"
          defaultMessage="Tours walk you through vines where green berries sun-dry into fragrant black peppercorns. Visitors can blend their own spice mix to take home."
          description="Fun fact detail shown while AI trip generation is in progress for Xuyen Moc (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="m8RpYfNsj5zJRuj0trYL"
          defaultMessage="Sea turtles nest on secluded sands"
          description="Fun fact headline shown while AI trip generation is in progress for Xuyen Moc (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="rrqq8B2HWtj2ejR-EMqr"
          defaultMessage="Community patrols relocate eggs to hatcheries, shielding them from monitor lizards. Hatchlings are released at sunset between April and October."
          description="Fun fact detail shown while AI trip generation is in progress for Xuyen Moc (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Tp3wSwkve0cixq-8pdUA"
          defaultMessage="Dragon-fruit farms glow under night lights"
          description="Fun fact headline shown while AI trip generation is in progress for Xuyen Moc (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="L5I1mwm81F4g7MmGajcs"
          defaultMessage="Illumination mimics moonlight, tricking cactus vines into off-season flowering and yielding two crops a year. The fields look like alien gardens from the highway."
          description="Fun fact detail shown while AI trip generation is in progress for Xuyen Moc (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="t4hiVZFVd3YR3vvdDNY1"
          defaultMessage="Operation Frequent Wind evacuees departed this coast in 1975"
          description="Fun fact headline shown while AI trip generation is in progress for Xuyen Moc (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="W8w0NPq0JdRDaRhl5D1G"
          defaultMessage="Thousands crammed aboard fishing junks to reach U.S. Navy ships offshore during the final days of the Vietnam War. Local elders still recount the hectic exodus."
          description="Fun fact detail shown while AI trip generation is in progress for Xuyen Moc (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="xsM7QF69DMasso78-gn7"
          defaultMessage="Ca trù folk songs echo at Bình Châu temple festivals"
          description="Fun fact headline shown while AI trip generation is in progress for Xuyen Moc (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ljeViFBTs-JW08Fynqz5"
          defaultMessage="Female vocalists weave praise poetry over bamboo clappers and lute. UNESCO lists the genre as Intangible Cultural Heritage in need of safeguarding."
          description="Fun fact detail shown while AI trip generation is in progress for Xuyen Moc (fact 8)"
        />
      ),
    },
  ],
  'Ho Tram': [
    {
      text: (
        <FormattedMessage
          id="gMJPJWJqFH4yeCtJmMJw"
          defaultMessage="The Grand Ho Tram was Vietnam’s first integrated casino resort"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Tram (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="kJyscshr805ZSIpKW4Gq"
          defaultMessage="Its twin towers now total over 1,100 rooms facing a 2.2 km private beach. Foreign passport holders can roll dice 24/7 on a Vegas-size gaming floor."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Tram (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="tkgUTwMg1xs-fE_7p6wt"
          defaultMessage="The Bluffs golf course ranks among Asia’s top five"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Tram (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ITfqeE2A3EjJNUuJFx04"
          defaultMessage="Greg Norman sculpted fairways through 50-m sand dunes with sweeping sea views. Wind shifts can change club choice by three shots on a single hole."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Tram (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="obcWgQeZIXBvu9wPOcmK"
          defaultMessage="Ho Tram served as a Viet Cong supply route"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Tram (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="-KoDcyxt3MR9Ks37xXCD"
          defaultMessage="Jungle paths linked coastal hamlets to the Hồ Chí Minh Trail. Remnants of hidden bunkers still dot casuarina groves behind the resort strip."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Tram (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="xf1OL18Y6nxr7z8YrjaW"
          defaultMessage="Local fishermen dry squid on bamboo racks daily"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Tram (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="3MaV18wXpMWUoFJNEKCW"
          defaultMessage="Sun-bleached strips are grilled roadside as the ultimate beer snack. Morning markets auction the freshest catches before tourists wake."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Tram (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="UiAKJO97EZSMySECant5"
          defaultMessage="Kitesurfers flock to steady onshore winds"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Tram (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="RBxqYM9XVFzXsFor1GH_"
          defaultMessage="Prime conditions blow from November to March when waves average 1–2 m. The town now hosts Vietnam’s National Kiteboarding Championships each February."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Tram (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="oUCrZdBw7zZ3N7ctUBH3"
          defaultMessage="Nearby Minh Đạm Mountain hides wartime tunnels"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Tram (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="KzZBq2D7GEEXuLjryoeF"
          defaultMessage="A summit pagoda offers panoramic coastal views after short but steep stair climbs. A small museum displays photos and recovered weapons."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Tram (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="T2bjEnFO2v30qludszpq"
          defaultMessage="Ho Tram’s mangroves teem with mudskippers and fiddler crabs"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Tram (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="tRKJ9k6rx1nD6MWyJtUG"
          defaultMessage="Kayak tours weave through emerald channels where kingfishers dive for shrimp. Researchers have recorded 74 bird species in the estuary."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Tram (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="lNQS-3jfFvpX8KTjwoCY"
          defaultMessage="A sea-turtle rehab centre opened in 2024"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Tram (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ZQy6UAngNDxLBUdP00d2"
          defaultMessage="Injured greens and hawksbills are nursed back to health before weekly beach releases. Visitors can sponsor satellite tags to track migration routes."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Tram (fact 8)"
        />
      ),
    },
  ],
  'Ho Chi Minh City': [
    {
      text: (
        <FormattedMessage
          id="qeiYE73DWDUq0uR_aPy1"
          defaultMessage="Over 8.9 million motorbikes buzz through the city"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Chi Minh City (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="fI9-KAKid7CIPB4zYIah"
          defaultMessage="That’s nearly one bike for every resident, forming a river of headlights at rush hour. Locals navigate the flow using subtle honk languages and hand signals."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Chi Minh City (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="YwByo11ajRwnndrkBjm6"
          defaultMessage="Saigon’s Central Post Office was designed by Eiffel’s firm"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Chi Minh City (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="y1puHR_pFODlYAkqnOR4"
          defaultMessage="Inside, wrought-iron vaults and marble floors evoke a 19th-century railway station. Antique phone booths still function along its polished walls."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Chi Minh City (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="eEKMAFnhhUwKhbUQpnsH"
          defaultMessage="Bến Thành Market has traded since 1870"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Chi Minh City (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="00CWhDoiCsua1upsutJL"
          defaultMessage="Its clock-tower facade is a beloved city emblem featuring on souvenir T-shirts. Vendors sell everything from lotus tea to knock-off sneakers under one steamy roof."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Chi Minh City (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="oXdGIktwEvtbjPpR1kkW"
          defaultMessage="Tanks burst through Reunification Palace gates on 30 April 1975"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Chi Minh City (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Pzn7u94c1vrxRhbWxWgo"
          defaultMessage="A replica star-emblazoned tank now sits on the lawn as a photogenic memorial. Inside, war-era map rooms and rotary phones remain frozen in time."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Chi Minh City (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="BF-Z58PT27peuIdogT-N"
          defaultMessage="Cà phê sữa đá blends coffee with condensed milk"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Chi Minh City (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="qzianH8UyRKWcv7NL9dI"
          defaultMessage="Cafés drip robusta through metal phin filters directly onto ice for 15 minutes. Sidewalk stools fill from dawn to midnight with caffeine devotees."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Chi Minh City (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="SXmc48ceF8nQlkv2x5Z2"
          defaultMessage="Bitexco Tower’s helipad juts out like a lotus petal"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Chi Minh City (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="QE66L0QjMSJW8YZw2Kyu"
          defaultMessage="At 262 m it was Vietnam’s tallest building in 2010, now out-soared by Landmark 81. The 49th-floor Skydeck delivers 360-degree city panoramas."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Chi Minh City (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="BMD4e9ApTnpg0nUz0WvP"
          defaultMessage="The city changed its name from Saigon in 1976"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Chi Minh City (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="gG2BloxWZ_elK34o3-sb"
          defaultMessage="It honours revolutionary leader President Hồ Chí Minh, yet locals still casually call the downtown core “Saigon.” Street signs display both names interchangeably."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Chi Minh City (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Wz9BqF2LIImPbWujQlqG"
          defaultMessage="Cu Chi Tunnels stretch 120 km beneath nearby forests"
          description="Fun fact headline shown while AI trip generation is in progress for Ho Chi Minh City (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="nj0tIGSD4sVFKQ8tw4qc"
          defaultMessage="Guerrillas cooked, lived and fought in this subterranean maze during the war. Today widened sections let tourists crawl through and sample cassava rations."
          description="Fun fact detail shown while AI trip generation is in progress for Ho Chi Minh City (fact 8)"
        />
      ),
    },
  ],
  Phuket: [
    {
      text: (
        <FormattedMessage
          id="z9WTN1jFcWs_RtUGsbZT"
          defaultMessage="Phuket is Thailand’s largest island"
          description="Fun fact headline shown while AI trip generation is in progress for Phuket (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="LWbteUjdIZKdj7bz657E"
          defaultMessage="Covering 543 km², it’s roughly the size of Singapore. The Sarasin and Thao Thep Krasattri bridges connect it to the mainland province of Phang Nga."
          description="Fun fact detail shown while AI trip generation is in progress for Phuket (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="tk5V4ox30KD64gCS9p6b"
          defaultMessage="The Big Buddha statue stands 45 m tall"
          description="Fun fact headline shown while AI trip generation is in progress for Phuket (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="RoB-Zeq5S8NO7P5pDhVp"
          defaultMessage="Clad in 135 t of Burmese white marble, it gleams in sunrise light on Nakkerd Hill. A steep road rewards climbers with sweeping views of Chalong Bay."
          description="Fun fact detail shown while AI trip generation is in progress for Phuket (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="zc5_fet1Pvev6MAiJT7Y"
          defaultMessage="Phuket Old Town flaunts sino-Portuguese mansions"
          description="Fun fact headline shown while AI trip generation is in progress for Phuket (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="yXlUYD24QFKhct36i1rR"
          defaultMessage="Tin-mining wealth funded these pastel shophouses in the 19th century. Many now host Peranakan museums, chic cafés and street-art murals."
          description="Fun fact detail shown while AI trip generation is in progress for Phuket (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="-8R09z5oRtBIyeJipZps"
          defaultMessage="The Vegetarian Festival features extreme body piercing"
          description="Fun fact headline shown while AI trip generation is in progress for Phuket (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="8ECyrruMRR4hrP-LB4sq"
          defaultMessage="Devotees walk on hot coals and skewer cheeks with swords to channel deities and banish evil. White-clad parades fill streets with firecrackers every October."
          description="Fun fact detail shown while AI trip generation is in progress for Phuket (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="EnEkcltjODG420_ZCjua"
          defaultMessage="Similan Islands off Phuket rank among the world’s top dive sites"
          description="Fun fact headline shown while AI trip generation is in progress for Phuket (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="VPs1hkzBve8pXJTWDIba"
          defaultMessage="Visibility can exceed 30 m in the Andaman Sea, revealing manta rays and whale sharks. Live-aboard boats run multi-day trips during November–May’s dry season."
          description="Fun fact detail shown while AI trip generation is in progress for Phuket (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Ge7uJWkgyWpZICa3KbTX"
          defaultMessage="Bangla Road pulses with neon nightlife"
          description="Fun fact headline shown while AI trip generation is in progress for Phuket (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="vOzYAu_tS5UcwfX2Ifs7"
          defaultMessage="The 400-m strip in Patong closes to traffic at sunset, exploding with go-go bars and LED street performers. Drink hawkers compete with EDM bass until dawn."
          description="Fun fact detail shown while AI trip generation is in progress for Phuket (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="XsIEsMOZVa3tRXEy9Z2B"
          defaultMessage="Phuket was a Maritime Silk Road trading hub"
          description="Fun fact headline shown while AI trip generation is in progress for Phuket (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="IS-Yjxyli4pEKzSaAtGy"
          defaultMessage="Arab, Indian and Chinese merchants once anchored for tin and spices, shaping a fusion cuisine. The yearly Old-Town Festival celebrates this multicultural heritage."
          description="Fun fact detail shown while AI trip generation is in progress for Phuket (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="_pTjWlbtwmwblbftPWP_"
          defaultMessage="Locals almost renamed the island 'Thalang' in 2011"
          description="Fun fact headline shown while AI trip generation is in progress for Phuket (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="RogI6ZdPwPpNmeG9MNO5"
          defaultMessage="The proposal aimed to revive Phuket’s pre-colonial name but stalled in parliament after tourism groups objected. “Phuket” therefore remains the global brand."
          description="Fun fact detail shown while AI trip generation is in progress for Phuket (fact 8)"
        />
      ),
    },
  ],
  Patong: [
    {
      text: (
        <FormattedMessage
          id="w6XIrpP1z12y-VfKycL2"
          defaultMessage="Patong Beach curves for almost 3 km"
          description="Fun fact headline shown while AI trip generation is in progress for Patong (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ZBqSQm_ohluyveTpcFjA"
          defaultMessage="Lifeguards mark safe swimming zones with red-yellow flags and jet-ski lanes. Parasailers colour the skyline during calm afternoon seas."
          description="Fun fact detail shown while AI trip generation is in progress for Patong (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="zkvsskcvYZn5yQQ2GtTS"
          defaultMessage="The 2004 tsunami hit Patong hard"
          description="Fun fact headline shown while AI trip generation is in progress for Patong (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="z1ejVwLYK2OrpztT1uqG"
          defaultMessage="Waves surged 6 m high, yet the town rebuilt within a year with new early-warning sirens. Evacuation routes and blue tsunami markers now line the streets."
          description="Fun fact detail shown while AI trip generation is in progress for Patong (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="zEWbAFLoDgPuNqbNkCL2"
          defaultMessage="Bangla Walking Street boasts over 200 bars"
          description="Fun fact headline shown while AI trip generation is in progress for Patong (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="PKQSV8dUpQFFsbqVZhaH"
          defaultMessage="LED signs, live bands and street magicians vie for attention amid thumping EDM. Patong’s nightlife runs until 4 a.m. on peak nights."
          description="Fun fact detail shown while AI trip generation is in progress for Patong (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="ps68C7NaFMKuB_QuuumW"
          defaultMessage="Freedom Beach hides just south of Patong"
          description="Fun fact headline shown while AI trip generation is in progress for Patong (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="xQnCyrB5plU_ZiqelfeV"
          defaultMessage="Reachable only by long-tail boat or a jungle trail, its entry fee funds daily waste collection. Powder-white sand stays surprisingly crowd-free even in high season."
          description="Fun fact detail shown while AI trip generation is in progress for Patong (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="VefL96ro1HKlQ2WbGwjy"
          defaultMessage="Jungceylon Mall is named after an old Western word for Phuket"
          description="Fun fact headline shown while AI trip generation is in progress for Patong (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="5xaOQ047VIjVZXYqxyEB"
          defaultMessage="A basement aquarium tunnel lets shoppers walk beneath tropical fish while escaping monsoon showers. Nightly fountain shows choreograph water jets to Thai pop."
          description="Fun fact detail shown while AI trip generation is in progress for Patong (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="osoCjl_mMCRltYbySuRk"
          defaultMessage="Patong Carnival kicks off high season every November"
          description="Fun fact headline shown while AI trip generation is in progress for Patong (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="hNIPNym9BlEC2ryFW-B_"
          defaultMessage="Floats depict sea dragons and Thai myths while fireworks light the bay. Hotels offer street-food stalls so visitors can taste local dishes in one stroll."
          description="Fun fact detail shown while AI trip generation is in progress for Patong (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="XDeSXzsDq91PXwIsn8Jh"
          defaultMessage="OTOP Market promotes 'One Tambon One Product'"
          description="Fun fact headline shown while AI trip generation is in progress for Patong (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="xqW9-JpFhS5IEUfZMpAT"
          defaultMessage="Stall fees funnel profits back to artisans’ home villages, supporting rural crafts. Handmade batik sarongs and coconut bowls are popular buys."
          description="Fun fact detail shown while AI trip generation is in progress for Patong (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="0v8GIzxgSl5ZCY4aemAo"
          defaultMessage="Tri Trang Beach shelters one of Patong’s last coral reefs"
          description="Fun fact headline shown while AI trip generation is in progress for Patong (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="eS8AXkOl1_KtSFfzn6j_"
          defaultMessage="Snorkellers find Nemo-like clownfish just metres offshore at low tide. Coral nurseries have been installed to revive damaged sections."
          description="Fun fact detail shown while AI trip generation is in progress for Patong (fact 8)"
        />
      ),
    },
  ],
  'Koh Samui': [
    {
      text: (
        <FormattedMessage
          id="hOngrl7bKYtNbEz3p6z5"
          defaultMessage="Samui Airport has thatched-roof open-air terminals"
          description="Fun fact headline shown while AI trip generation is in progress for Koh Samui (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="mDfh0w5Olayx3S6QixBU"
          defaultMessage="Privately owned by Bangkok Airways, it feels more like a resort than an airport. Passengers taxi past frangipani gardens instead of concrete jetways."
          description="Fun fact detail shown while AI trip generation is in progress for Koh Samui (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="B_RQVXpJImpo-f6aI9b9"
          defaultMessage="The island exports two million coconuts a month"
          description="Fun fact headline shown while AI trip generation is in progress for Koh Samui (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="0f-f-_C9mkHuoibUyUSe"
          defaultMessage="Trucks ferry them to Surat Thani for processing into oil, milk and sugar. Trained macaques once harvested many of the nuts, though mechanical pickers are now common."
          description="Fun fact detail shown while AI trip generation is in progress for Koh Samui (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="SOWIAbctrKAe3FNHe-Rk"
          defaultMessage="Big Buddha (Wat Phra Yai) rises 12 m above the sea"
          description="Fun fact headline shown while AI trip generation is in progress for Koh Samui (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="u8-xfHPkl3SGPGQ_znOj"
          defaultMessage="Built in 1972, the golden statue is linked to the mainland by a short causeway. Its steep staircase offers sweeping photo ops at sunrise."
          description="Fun fact detail shown while AI trip generation is in progress for Koh Samui (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="xLEdH5wxewCPtu77LDM8"
          defaultMessage="Full-moon parties began on nearby Koh Phangan"
          description="Fun fact headline shown while AI trip generation is in progress for Koh Samui (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="tG3i4saXATfbq_FgHqQk"
          defaultMessage="High-speed ferries shuttle revellers across the channel all night for beachfront EDM. Dawn boats return crowds clutching fluorescent paint and sand-filled shoes."
          description="Fun fact detail shown while AI trip generation is in progress for Koh Samui (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Fc6883huohoYQY5JJW-0"
          defaultMessage="The 'Buddha’s Magic Garden' hides at 500 m altitude"
          description="Fun fact headline shown while AI trip generation is in progress for Koh Samui (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="v4lcuoZwNYK-rPXzuZpi"
          defaultMessage="Farmer-sculptor Nim Thongsuk spent 1976-87 carving hundreds of mossy statues in a jungle clearing. Trickling waterfalls and mist lend the site an otherworldly feel."
          description="Fun fact detail shown while AI trip generation is in progress for Koh Samui (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="SPYt8nBCWLwdcv2iBmCi"
          defaultMessage="Samui Regatta attracts 500 sailors each May"
          description="Fun fact headline shown while AI trip generation is in progress for Koh Samui (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="sL1qnD068EgCOVPHvRA3"
          defaultMessage="Races loop around neighbouring islands, boosting the Gulf’s yachting profile ahead of major Asian events. Spectators watch colourful spinnakers from Chaweng’s shore-side bars."
          description="Fun fact detail shown while AI trip generation is in progress for Koh Samui (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="87V3xujNoV49FRlWCVIZ"
          defaultMessage="Hin Ta and Hin Yai rocks resemble male and female anatomy"
          description="Fun fact headline shown while AI trip generation is in progress for Koh Samui (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="8J2SeG2wosVJoBFyFBRN"
          defaultMessage="Legend says star-crossed lovers were turned to stone by sea spirits, explaining the suggestive shapes. Vendors nearby sell phallic coconut candies for a cheeky souvenir."
          description="Fun fact detail shown while AI trip generation is in progress for Koh Samui (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="4FmstmuZauvkMgwt7L7Y"
          defaultMessage="Samui banned single-use plastic bags in 2015"
          description="Fun fact headline shown while AI trip generation is in progress for Koh Samui (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="3khz1ireKGkOQNZNn9TO"
          defaultMessage="Reusable bamboo straws and cloth totes are now standard in markets and cafés. The initiative inspired other Thai islands to adopt similar eco-measures."
          description="Fun fact detail shown while AI trip generation is in progress for Koh Samui (fact 8)"
        />
      ),
    },
  ],
  Generic: [
    {
      text: (
        <FormattedMessage
          id="ZRvAc09lckT8AW3i75Fo"
          defaultMessage="Airplane food tastes different on purpose"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 1)"
        />
      ),
      detail: (
        <FormattedMessage
          id="mZuEGoDQDoRGcT9h0e_E"
          defaultMessage="Cabin air dulls taste buds and dries noses, so airlines often double the salt to compensate."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 1)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="3ionmAQADFNsJwB00D0Z"
          defaultMessage="There are nearly 30 places named &quot;London&quot; around the world"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 2)"
        />
      ),
      detail: (
        <FormattedMessage
          id="Q_R8SqG0Zs55mhB3LzI7"
          defaultMessage="Beyond the UK capital, you’ll find Londons in Canada, Ohio, Kiribati and more."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 2)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="wYNCXP0ggBJU9axAK7vs"
          defaultMessage="The world’s most visited city isn’t New York or Paris"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 3)"
        />
      ),
      detail: (
        <FormattedMessage
          id="i0xiB-ccGiBKs8H7UJMq"
          defaultMessage="Bangkok regularly tops global tourism charts, thanks to its food, nightlife and affordability."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 3)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="KOhJ64Uav_cvgw-5w9ZB"
          defaultMessage="The London Eye was never meant to be permanent"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 4)"
        />
      ),
      detail: (
        <FormattedMessage
          id="U1Lrx9pXUZPewERvN4dF"
          defaultMessage="Built for the millennial celebrations in 2000, the wheel proved so popular it stayed."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 4)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="j9nhYb-pv9_H_vKipznr"
          defaultMessage="The world’s shortest scheduled flight takes under 60 seconds"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 5)"
        />
      ),
      detail: (
        <FormattedMessage
          id="34NgMDBxfYR4s_IyVNSw"
          defaultMessage="Loganair’s Westray–Papa Westray hop in Scotland spans just 2.7 km."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 5)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="1lQre_r-KdhVONcBZJIU"
          defaultMessage="Travel supports more than one in ten jobs worldwide"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 6)"
        />
      ),
      detail: (
        <FormattedMessage
          id="ijk_jc-EJon__h9SyKJ4"
          defaultMessage="Pre-pandemic, tourism employed roughly 330 million people across the globe."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 6)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="vbzokcmqJX6-YLy-BHUI"
          defaultMessage="Rome hosts a museum devoted entirely to pasta"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 7)"
        />
      ),
      detail: (
        <FormattedMessage
          id="MdOjuN2Zth1a5kL77XLd"
          defaultMessage="The Museo della Pasta has 11 rooms exploring its history, science, shapes and art."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 7)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="pORaQoKw4jo0x9xg4TF_"
          defaultMessage="The Eiffel Tower was nearly torn down—until radio transmissions saved it"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 8)"
        />
      ),
      detail: (
        <FormattedMessage
          id="RaO-NnMkt8XX9iC1w9QV"
          defaultMessage="Slated for removal in 1909, its new role as a strategic radio mast secured its future."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 8)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="RgQ4qwoHLsJKYLAYt6d6"
          defaultMessage="Some airports ignore daylight saving time"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 9)"
        />
      ),
      detail: (
        <FormattedMessage
          id="RibJtjiAyjI_CdHrUJnI"
          defaultMessage="Phoenix Sky Harbor, for instance, stays on Mountain Standard Time all year."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 9)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="qcO2E9aj934Nq420esWb"
          defaultMessage="You can fly around the world faster going east"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 10)"
        />
      ),
      detail: (
        <FormattedMessage
          id="rEobkhRSrWrfDyBvX1Wd"
          defaultMessage="Westerly jet streams give eastbound aircraft a helpful tail-wind that can slice hours off long-haul trips."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 10)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Zpx9az2937Qdpw1oWard"
          defaultMessage="Finland has almost as many saunas as cars"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 11)"
        />
      ),
      detail: (
        <FormattedMessage
          id="GnrY3S2v16Y0fKyZKwQy"
          defaultMessage="Around 3 million saunas serve 5.5 million Finns—close to the nation’s 3.7 million passenger vehicles."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 11)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="GzS3QJnOPpv-Yc8DRzYU"
          defaultMessage="Tromsø, Norway sees no sunrise for six weeks"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 12)"
        />
      ),
      detail: (
        <FormattedMessage
          id="3OAKDdx7Qr-ulZhTSXn2"
          defaultMessage="During polar night (late November to mid-January) the sun never rises above the horizon."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 12)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="IyTGTZoG1ZJQJX_nSBMS"
          defaultMessage="Switzerland technically has no capital city"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 13)"
        />
      ),
      detail: (
        <FormattedMessage
          id="2FGJJw8pfGk4vs7l2eIr"
          defaultMessage="Bern hosts parliament, but the Swiss constitution never designates an official capital."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 13)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="5C2rRprn9QwPqFvY4LDi"
          defaultMessage="The Great Wall of China isn’t a single wall"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 14)"
        />
      ),
      detail: (
        <FormattedMessage
          id="zurQ398j46Fzl0ElxQ_K"
          defaultMessage="It’s a network of fortifications stretching about 21,000 km across northern China."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 14)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="AtBvwJq92BCE8DpmH4lG"
          defaultMessage="Malana, India, speaks a mysterious local dialect"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 15)"
        />
      ),
      detail: (
        <FormattedMessage
          id="isCFBiBbGmpe05jApIZE"
          defaultMessage="Kanashi is unique to the village; legends link it to Greek soldiers, but linguists trace Tibeto-Burman roots."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 15)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="isyMnMkTyl6YX2FCDW1M"
          defaultMessage="Monaco is smaller than New York’s Central Park"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 16)"
        />
      ),
      detail: (
        <FormattedMessage
          id="5nDkk2Bb73HoT3Uz-uoE"
          defaultMessage="The principality covers 2.02 km², while Central Park spans roughly 3.4 km²."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 16)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="4FdkGJvmHBsIOAhvmvaN"
          defaultMessage="You can ride a zipline from Spain to Portugal"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 17)"
        />
      ),
      detail: (
        <FormattedMessage
          id="qpYNpyYUw6mH4eM8JvmT"
          defaultMessage="Limite Zero glides over the Guadiana River—the world’s only cross-border zipline."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 17)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="lLO1meqU8yDr6toCT6Dy"
          defaultMessage="Canada has more lakes than the rest of the world combined"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 18)"
        />
      ),
      detail: (
        <FormattedMessage
          id="g-x3nbOEP1V03F1vIRKc"
          defaultMessage="Roughly 60% of the planet’s lakes ≥ 0.1 km² lie within Canadian borders."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 18)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="qe8FhD0WwzPETzp5Xllu"
          defaultMessage="La Gomera in the Canaries has a whistled language"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 19)"
        />
      ),
      detail: (
        <FormattedMessage
          id="qdYhrcL0GWoY-K_UIwrA"
          defaultMessage="Silbo Gomero lets residents converse across deep valleys using only whistles."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 19)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="GkEwbzvtlywtpa8Q_zje"
          defaultMessage="Bhutan measures progress in Gross National Happiness"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 20)"
        />
      ),
      detail: (
        <FormattedMessage
          id="xUjYRIa--gmO48GmJ7RY"
          defaultMessage="The index guides government decisions alongside traditional economic metrics."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 20)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="s1fJSOjoLmsR1wbXkUBw"
          defaultMessage="London cabbies memorise around 25,000 streets"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 21)"
        />
      ),
      detail: (
        <FormattedMessage
          id="spxqyrzpLtvFm4tMHa0c"
          defaultMessage="Earning ‘The Knowledge’ licence can take years of scooter-based studying."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 21)"
        />
      ),
    },
    {
      text: (
        <FormattedMessage
          id="Ve50eSD40z8GPWknzzXT"
          defaultMessage="You can mail a coconut in Hawaii with no packaging"
          description="Fun fact headline shown while AI trip generation is in progress for Generic (fact 22)"
        />
      ),
      detail: (
        <FormattedMessage
          id="qAIrprqZ5gI_bBIe_QO-"
          defaultMessage="Write an address, add stamps and the USPS will deliver the bare nut—no box required."
          description="Fun fact detail shown while AI trip generation is in progress for Generic (fact 22)"
        />
      ),
    },
  ],
}

export const fallbackImageIds: Array<string> = [
  '5ozks72rwzry7f9k2m9n',
  'rpuuftaccpy35ymgxn86',
  'pm9wsigf28xbtjdrrnv',
  'khdm8sqv4llroip0gr3fa',
  'o67q4bymkgw9zeo7gzg',
]

export const stepImageIds: Record<AITripPreferenceStep, string | undefined> = {
  start: undefined,
  location: undefined,
  occupancy: 'mt1tdpsaa8j2mobp81z',
  datePicker: '7hifc74i83p7kds2fp',
  pace: '9w6i5jqu19hhyebmmzo',
  proximity: 'cnzq14sdc2j0afsjvvr',
  budget: 'kkflnjvd7sradtskysz',
  theme: 'a51onk9awf9id7rtcnmu',
  mustDo: '9ktjc7j8r7tqztli96v',
  foodStyle: 'nrhcjilarfw651sgkkmn',
  freeText: undefined,
}

export const animatedBackgroundIds = [
  'trip-fade-1',
  'trip-fade-2',
  'trip-fade-3',
]

export function AITripTitles(hotelCity: string) {
  return [
    <FormattedMessage
      key="shaping-itinerary"
      id="XIPRyf10JRcFEf9mClRI"
      defaultMessage="We’re shaping your <i>{city}</i> itinerary..."
      description="Loading message while AI shapes the trip itinerary"
      values={{
        city: hotelCity,
        i: (chunks) => <i>{chunks}</i>,
      }}
    />,
    <FormattedMessage
      key="understanding-preferences"
      id="bl6p9AglUnKEKiSqId7B"
      defaultMessage="Understanding your trip preferences and travel style..."
      description="AI trip generation loading message about understanding user preferences"
    />,
    <FormattedMessage
      key="reading-reviews"
      id="ERQqIDOqXvDt4dAgjyIz"
      defaultMessage="Reading reviews, blogs and local guides..."
      description="AI trip generation loading message about reading reviews and guides"
    />,
    <FormattedMessage
      key="researching-things-to-do"
      id="8llhiFvY6i6ZLJsfAvhv"
      defaultMessage="Researching the best things to do..."
      description="AI trip generation loading message about researching activities to do"
    />,
    <FormattedMessage
      key="finding-restaurants"
      id="51_xG7OTUyJqaTUHqiwJ"
      defaultMessage="Finding restaurants and attractions near your hotel..."
      description="AI trip generation loading message about finding nearby restaurants and attractions"
    />,
    <FormattedMessage
      key="curating-experiences"
      id="s9VlBgoaOAOjMaFBy4DE"
      defaultMessage="Curating the right experiences for each day..."
      description="AI trip generation loading message about curating daily experiences for the trip"
    />,
    <FormattedMessage
      key="checking-timings"
      id="576qpVdG0KtRQtJqg-kn"
      defaultMessage="Checking timings and transfers so your day flows smoothly..."
      description="AI trip generation loading message about checking timings and logistics"
    />,
    <FormattedMessage
      key="finding-hidden-gems"
      id="_giVZG04l9fap4abYxJx"
      defaultMessage="Finding hidden gems beyond the tourist spots..."
      description="AI trip generation loading message about finding hidden gems off the beaten path"
    />,
    <FormattedMessage
      key="personalising-trip"
      id="qy_exhGB-MD2l-5GWipl"
      defaultMessage="Personalising your trip to match your style..."
      description="AI trip generation loading message about personalising the trip to user style"
    />,
    <FormattedMessage
      key="making-moments-count"
      id="ZDnqDMkSVGPOB6_sEvhV"
      defaultMessage="Making sure every moment counts..."
      description="AI trip generation loading message about making sure every moment counts"
    />,
    <FormattedMessage
      key="scouring-insider-tips"
      id="6nFBq9lGa1xG8dzlgZpc"
      defaultMessage="Scouring the internet for the best insider tips from locals..."
      description="AI trip generation loading message about scouring for insider tips from locals"
    />,
    <FormattedMessage
      key="double-checking"
      id="9xe0FVnSLCUJb9_ZXhcF"
      defaultMessage="Double-checking details so nothing gets missed..."
      description="AI trip generation loading message about double-checking all the details"
    />,
    <FormattedMessage
      key="final-touches"
      id="rx4Tqt3UkoxWe8DPimBK"
      defaultMessage="Adding the final touches..."
      description="AI trip generation loading message about adding the final touches to the itinerary"
    />,
  ]
}
